<template>
  <hs-modal
    :model-value="isOpen"
    title="Edit EBM Data"
    :description="
      isPurchaseDataDisabled
        ? 'Edit product data for EBM submission. Purchase data is read-only.'
        : 'Edit product and purchase data for EBM submission.'
    "
    size="xl"
    body-class="ebm-data-editor-modal__body"
    @close="handleClose"
  >
    <div class="ebm-data-editor-modal">
        <!-- Loading Overlay -->
        <div
          v-if="isLoadingData"
          class="absolute inset-0 bg-white dark:bg-slate-800 bg-opacity-95 flex flex-col items-center justify-center z-50 rounded-lg"
        >
          <div class="flex flex-col items-center space-y-4">
            <!-- Animated Spinner -->
            <div class="relative">
              <div
                class="w-16 h-16 border-4 border-blue-200 dark:border-blue-800 rounded-full animate-spin"
              ></div>
              <div
                class="absolute top-0 left-0 w-16 h-16 border-4 border-transparent border-t-blue-600 dark:border-t-blue-400 rounded-full animate-spin"
              ></div>
            </div>

            <!-- Loading Message -->
            <div class="text-center">
              <h3
                class="text-lg font-semibold text-gray-900 dark:text-white mb-2"
              >
                {{ loadingMessage }}
              </h3>
              <p class="text-sm text-gray-600 dark:text-gray-400">
                Fetching EBM metadata and preparing form...
              </p>
            </div>

            <!-- Progress Steps -->
            <div class="flex space-x-2">
              <div
                class="w-2 h-2 bg-blue-600 dark:bg-blue-400 rounded-full animate-pulse"
              ></div>
              <div
                class="w-2 h-2 bg-blue-600 dark:bg-blue-400 rounded-full animate-pulse"
                style="animation-delay: 0.2s"
              ></div>
              <div
                class="w-2 h-2 bg-blue-600 dark:bg-blue-400 rounded-full animate-pulse"
                style="animation-delay: 0.4s"
              ></div>
            </div>
          </div>
        </div>

        <!-- Loading state -->
        <div v-if="loading" class="flex justify-center items-center py-8">
          <vue-feather
            type="loader"
            size="32"
            class="animate-spin text-blue-600"
          />
          <span class="ml-2 text-gray-600 dark:text-gray-300"
            >Loading data...</span
          >
        </div>

        <!-- Content -->
        <div v-else class="flex-1 overflow-y-auto px-6 space-y-6">
          <!-- EBM Already Saved Warning -->
          <div
            v-if="isEditingDisabled"
            class="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4"
          >
            <div class="flex">
              <div class="flex-shrink-0">
                <vue-feather
                  type="alert-triangle"
                  class="h-5 w-5 text-yellow-400"
                />
              </div>
              <div class="ml-3">
                <h3
                  class="text-sm font-medium text-yellow-800 dark:text-yellow-200"
                >
                  EBM Data Already Saved
                </h3>
                <div class="mt-2 text-sm text-yellow-700 dark:text-yellow-300">
                  <p>
                    The purchase data has already been saved to EBM and cannot
                    be edited. The form is in read-only mode.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <!-- Error Messages -->
          <div
            v-if="validationErrors.length > 0"
            class="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4"
          >
            <div class="flex">
              <div class="flex-shrink-0">
                <vue-feather type="alert-circle" class="h-5 w-5 text-red-400" />
              </div>
              <div class="ml-3">
                <h3 class="text-sm font-medium text-red-800 dark:text-red-200">
                  Validation Errors
                </h3>
                <div class="mt-2 text-sm text-red-700 dark:text-red-300">
                  <ul class="list-disc list-inside space-y-1">
                    <li v-for="error in validationErrors" :key="error.field">
                      {{ error.message }}
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          <!-- Information Messages -->
          <div
            v-if="activeTab === 'product' && !hasProductPurchaseId"
            class="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4"
          >
            <div class="flex">
              <div class="flex-shrink-0">
                <vue-feather type="info" class="h-5 w-5 text-blue-400" />
              </div>
              <div class="ml-3">
                <h3
                  class="text-sm font-medium text-blue-800 dark:text-blue-200"
                >
                  Save Information
                </h3>
                <div class="mt-2 text-sm text-blue-700 dark:text-blue-300">
                  <ul class="list-disc list-inside space-y-1">
                    <li v-if="!hasProductPurchaseId">
                      Product Purchase is not available. You can save the
                      product data only.
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          <!-- Quantity Mismatch Warning -->
          <div
            v-if="hasQuantityMismatch && !isPurchaseDataDisabled"
            class="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-3"
          >
            <div class="flex items-start space-x-4">
              <vue-feather
                type="alert-triangle"
                class="h-5 w-5 text-yellow-400 flex-shrink-0 mt-0.5"
              />
              <div class="flex-1 max-w-md">
                <span
                  class="text-sm font-medium text-yellow-800 dark:text-yellow-200"
                >
                  Quantity Mismatch Alert:
                </span>


              </div>
              <div class="flex items-center space-x-4 flex-shrink-0">
                <!-- Original Value Checkbox Option -->
                <label class="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="checkbox"
                    v-model="selectedQuantityOptions"
                    disabled
                    value="original"
                    class="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    @change="handleQuantityOptionChange"
                  />
                  <span class="text-sm text-gray-700 dark:text-gray-300">
                    Last Purchase:
                    <strong>{{
                      formatDisplayNumber(originalPurchaseQuantity)
                    }}</strong>
                  </span>
                </label>

                <!-- Corrected Value Checkbox Option -->
                <label class="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="checkbox"
                    v-model="selectedQuantityOptions"
                    disabled
                    value="corrected"
                    class="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    @change="handleQuantityOptionChange"
                  />
                  <span class="text-sm text-gray-700 dark:text-gray-300">
                    Current Inventory:
                    <strong>{{
                      formatDisplayNumber(correctedPurchaseQuantity)
                    }}</strong>
                  </span>
                </label>
              </div>
            </div>
                            <div class="mt-1 max-w-[666px]">
                  <button
                    @click="
                      showQuantityMismatchDetails = !showQuantityMismatchDetails
                    "
                    class="text-xs text-yellow-700 dark:text-yellow-300 hover:text-yellow-800 dark:hover:text-yellow-200 underline focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:ring-opacity-50 rounded"
                  >
                    <strong>Read more</strong>
                    <vue-feather
                      :type="
                        showQuantityMismatchDetails
                          ? 'chevron-up'
                          : 'chevron-down'
                      "
                      size="12"
                      class="inline ml-1"
                    />
                  </button>
                  <div
                    v-if="showQuantityMismatchDetails"
                    class="text-xs text-yellow-700 dark:text-yellow-300 mt-2 p-2 bg-yellow-100 dark:bg-yellow-800/30 rounded border border-yellow-200 dark:border-yellow-700"
                  >
                    To have a fresh start with RRA EBM, we require using the
                    Current Inventory quantity. This ensures your EBM data
                    reflects the actual stock levels in your system and helps
                    maintain accurate tax reporting and compliance with RRA
                    requirements. If you want to remove damaged items or items that are not in stock, you can do so by using the app "Remove Items from Inventory" option.
                  </div>
                </div>
          </div>

          <!-- Tabs -->
          <div class="border-b border-gray-200 dark:border-gray-600">
            <nav class="-mb-px flex space-x-8">
              <button
                v-for="tab in tabs"
                :key="tab.id"
                @click="!isTabDisabled(tab.id) && (activeTab = tab.id)"
                :disabled="isTabDisabled(tab.id)"
                :class="[
                  activeTab === tab.id
                    ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300',
                  'whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm',
                  isTabDisabled(tab.id)
                    ? 'opacity-50 cursor-not-allowed'
                    : 'cursor-pointer',
                ]"
              >
                {{ tab.name }}
                <span
                  v-if="isTabDisabled(tab.id)"
                  class="ml-2 text-xs text-gray-400"
                >
                  (Disabled)
                </span>
              </button>
            </nav>
          </div>

          <!-- EBM Item Data Tab -->
          <div v-if="activeTab === 'product'" class="space-y-6">
            <!-- Level 1: Essential Overview -->
            <div
              class="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-xl p-6 border border-blue-200 dark:border-blue-700"
            >
              <div class="flex items-center justify-between mb-6">
                <div class="flex items-center">
                  <div
                    class="flex items-center justify-center w-8 h-8 bg-blue-600 text-white rounded-full text-sm font-semibold mr-3"
                  >
                    1
                  </div>
                  <h4
                    class="text-lg font-semibold text-gray-900 dark:text-white"
                  >
                    Essential Overview
                  </h4>
                </div>
                <div
                  class="text-sm text-blue-600 dark:text-blue-400 font-medium"
                >
                  Core product information
                </div>
              </div>

              <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                <!-- Item Name -->
                <div class="col-span-1 md:col-span-2">
                  <label
                    class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                  >
                    Item Name *
                  </label>
                  <input
                    v-model="formData.ebmItem.itemNm"
                    type="text"
                    maxlength="200"
                    class="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-800 dark:text-white transition-all duration-200"
                    :class="{
                      'border-red-500 ring-red-500':
                        hasFieldError('ebmItem.itemNm'),
                    }"
                    placeholder="Enter product name"
                  />
                </div>

                <!-- Item Code -->
                <div>
                  <label
                    class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                  >
                    Item Code *
                    <span
                      v-if="isAlwaysLockedFieldDisabled"
                      class="text-xs text-orange-600 dark:text-orange-400 ml-2"
                    >
                      (Locked)
                    </span>
                  </label>
                  <input
                    v-model="formData.ebmItem.itemCd"
                    type="text"
                    maxlength="20"
                    :disabled="true"
                    class="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-800 dark:text-white disabled:bg-gray-100 disabled:text-gray-500 dark:disabled:bg-gray-700 dark:disabled:text-gray-400"
                    :class="{
                      'border-red-500 ring-red-500':
                        hasFieldError('ebmItem.itemCd'),
                    }"
                    placeholder="Auto-generated"
                  />
                </div>

                <!-- Origin Country Code -->
                <div>
                  <EbmSelectDropdown
                    field-name="orgnNatCd"
                    v-model="formData.ebmItem.orgnNatCd"
                    label="Origin Country Code *"
                    placeholder="Select country..."
                    :disabled="isAlwaysLockedFieldDisabled"
                    :has-error="hasFieldError('ebmItem.orgnNatCd')"
                    :show-locked-message="true"
                    :is-locked="isAlwaysLockedFieldDisabled"
                  />
                </div>

                <!-- Packaging Unit Code -->
                <div>
                  <EbmSelectDropdown
                    field-name="pkgUnitCd"
                    v-model="formData.ebmItem.pkgUnitCd"
                    label="Packaging Unit Code *"
                    placeholder="Select unit..."
                    :disabled="isAlwaysLockedFieldDisabled"
                    :has-error="hasFieldError('ebmItem.pkgUnitCd')"
                    :show-locked-message="true"
                    :is-locked="isAlwaysLockedFieldDisabled"
                  />
                </div>

                <!-- Quantity Unit Code -->
                <div>
                  <EbmSelectDropdown
                    field-name="qtyUnitCd"
                    v-model="formData.ebmItem.qtyUnitCd"
                    label="Quantity Unit Code *"
                    placeholder="Select unit..."
                    :disabled="isAlwaysLockedFieldDisabled"
                    :has-error="hasFieldError('ebmItem.qtyUnitCd')"
                    :show-locked-message="true"
                    :is-locked="isAlwaysLockedFieldDisabled"
                  />
                </div>

                <!-- Taxation Type Code -->
                <div>
                  <EbmSelectDropdown
                    field-name="taxTyCd"
                    v-model="formData.ebmItem.taxTyCd"
                    label="Taxation Type Code *"
                    placeholder="Select tax type..."
                    :has-error="hasFieldError('ebmItem.taxTyCd')"
                  />
                </div>

                <!-- Default Unit Price -->
                <div>
                  <label
                    class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                  >
                    Sale Unit Price *
                  </label>
                  <input
                    v-model="formData.ebmItem.dftPrc"
                    type="text"
                    class="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-800 dark:text-white transition-all duration-200"
                    :class="{
                      'border-red-500 ring-red-500':
                        hasFieldError('ebmItem.dftPrc'),
                    }"
                    placeholder="Enter price"
                  />
                </div>
              </div>
            </div>

            <!-- Level 2: Key Product Metadata (Expandable) -->
            <div
              class="bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 rounded-xl border border-green-200 dark:border-green-700 overflow-hidden"
            >
              <button
                @click="showLevel2Fields = !showLevel2Fields"
                class="w-full p-6 text-left hover:bg-green-100 dark:hover:bg-green-800/30 transition-colors duration-200"
              >
                <div class="flex items-center justify-between">
                  <div class="flex items-center">
                    <div
                      class="flex items-center justify-center w-8 h-8 bg-green-600 text-white rounded-full text-sm font-semibold mr-3"
                    >
                      2
                    </div>
                    <div>
                      <h4
                        class="text-lg font-semibold text-gray-900 dark:text-white"
                      >
                        Key Product Metadata
                      </h4>
                      <p
                        class="text-sm text-green-600 dark:text-green-400 mt-1"
                      >
                        Additional product details and classifications
                      </p>
                    </div>
                  </div>
                  <div class="flex items-center">
                    <span
                      class="text-sm text-green-600 dark:text-green-400 mr-3"
                    >
                      View more details 2/3
                    </span>
                    <vue-feather
                      :type="showLevel2Fields ? 'chevron-up' : 'chevron-down'"
                      size="20"
                      class="text-green-600 dark:text-green-400 transition-transform duration-200"
                      :class="{ 'rotate-180': showLevel2Fields }"
                    />
                  </div>
                </div>
              </button>

              <div
                v-if="showLevel2Fields"
                class="px-6 pb-6 space-y-6 animate-fadeIn"
              >
                <!-- Locked Fields Section -->
                <div
                  class="bg-orange-50 dark:bg-orange-900/20 rounded-lg p-4 border border-orange-200 dark:border-orange-700"
                >
                  <h5
                    class="text-sm font-semibold text-orange-800 dark:text-orange-300 mb-3 flex items-center"
                  >
                    <vue-feather type="lock" size="16" class="mr-2" />
                    System Locked Fields
                  </h5>
                  <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <!-- TIN -->
                    <div>
                      <label
                        class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                      >
                        TIN *
                        <span
                          class="text-xs text-orange-600 dark:text-orange-400 ml-2"
                        >
                          (Locked - EBM settings data)
                        </span>
                      </label>
                      <input
                        v-model="formData.ebmItem.tin"
                        type="text"
                        maxlength="9"
                        :disabled="true"
                        class="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-800 dark:text-white disabled:bg-gray-100 disabled:text-gray-500 dark:disabled:bg-gray-700 dark:disabled:text-gray-400"
                        :class="{
                          'border-red-500 ring-red-500':
                            hasFieldError('ebmItem.tin'),
                        }"
                        placeholder="Auto-filled from settings"
                      />
                    </div>

                    <!-- Branch ID -->
                    <div>
                      <label
                        class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                      >
                        Branch ID *
                        <span
                          class="text-xs text-orange-600 dark:text-orange-400 ml-2"
                        >
                          (Locked - EBM settings data)
                        </span>
                      </label>
                      <input
                        v-model="formData.ebmItem.bhfId"
                        type="text"
                        maxlength="2"
                        :disabled="true"
                        class="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-800 dark:text-white disabled:bg-gray-100 disabled:text-gray-500 dark:disabled:bg-gray-700 dark:disabled:text-gray-400"
                        :class="{
                          'border-red-500 ring-red-500':
                            hasFieldError('ebmItem.bhfId'),
                        }"
                        placeholder="Auto-filled from settings"
                      />
                    </div>
                  </div>
                </div>

                <!-- Editable Fields Section -->
                <div
                  class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4"
                >
                  <!-- Item Type Code -->
                  <div>
                    <EbmSelectDropdown
                      field-name="itemTyCd"
                      v-model="formData.ebmItem.itemTyCd"
                      label="Item Type Code *"
                      placeholder="Search item types..."
                      :disabled="isAlwaysLockedFieldDisabled"
                      :has-error="hasFieldError('ebmItem.itemTyCd')"
                      :show-locked-message="true"
                      :is-locked="isAlwaysLockedFieldDisabled"
                    />
                  </div>

                  <!-- Item Classification Code -->
                  <div>
                    <EbmSelectDropdown
                      field-name="itemClsCd"
                      v-model="formData.ebmItem.itemClsCd"
                      label="Item Classification Code *"
                      placeholder="Search classification codes..."
                      :has-error="hasFieldError('ebmItem.itemClsCd')"
                    />
                  </div>

                  <!-- Barcode -->
                  <div>
                    <label
                      class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                    >
                      Barcode
                    </label>
                    <input
                      v-model="formData.ebmItem.bcd"
                      type="text"
                      maxlength="20"
                      class="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-800 dark:text-white transition-all duration-200"
                      :class="{
                        'border-red-500 ring-red-500':
                          hasFieldError('ebmItem.bcd'),
                      }"
                      placeholder="Enter barcode"
                    />
                  </div>

                  <!-- Insurance Applicability -->
                  <div>
                    <label
                      class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                    >
                      Insurance Applicability *
                    </label>
                    <select
                      v-model="formData.ebmItem.isrcAplcbYn"
                      class="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-800 dark:text-white transition-all duration-200"
                      :class="{
                        'border-red-500 ring-red-500': hasFieldError(
                          'ebmItem.isrcAplcbYn'
                        ),
                      }"
                    >
                      <option value="Y">Yes</option>
                      <option value="N">No</option>
                    </select>
                  </div>

                  <!-- Used/Unused Flag -->
                  <div>
                    <label
                      class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                    >
                      Used/Unused Flag *
                    </label>
                    <select
                      v-model="formData.ebmItem.useYn"
                      class="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-800 dark:text-white transition-all duration-200"
                      :class="{
                        'border-red-500 ring-red-500':
                          hasFieldError('ebmItem.useYn'),
                      }"
                    >
                      <option value="Y">Yes</option>
                      <option value="N">No</option>
                    </select>
                  </div>

                  <!-- Item Standard Name -->
                  <div>
                    <label
                      class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                    >
                      Item Standard Name
                    </label>
                    <input
                      v-model="formData.ebmItem.itemStdNm"
                      type="text"
                      maxlength="200"
                      class="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-800 dark:text-white transition-all duration-200"
                      :class="{
                        'border-red-500 ring-red-500':
                          hasFieldError('ebmItem.itemStdNm'),
                      }"
                      placeholder="Enter standard name"
                    />
                  </div>
                </div>
              </div>
            </div>

            <!-- Level 3: Pricing & Batch Details (Expandable) -->
            <div
              class="bg-gradient-to-r from-purple-50 to-violet-50 dark:from-purple-900/20 dark:to-violet-900/20 rounded-xl border border-purple-200 dark:border-purple-700 overflow-hidden"
            >
              <button
                @click="showLevel3Fields = !showLevel3Fields"
                class="w-full p-6 text-left hover:bg-purple-100 dark:hover:bg-purple-800/30 transition-colors duration-200"
              >
                <div class="flex items-center justify-between">
                  <div class="flex items-center">
                    <div
                      class="flex items-center justify-center w-8 h-8 bg-purple-600 text-white rounded-full text-sm font-semibold mr-3"
                    >
                      3
                    </div>
                    <div>
                      <h4
                        class="text-lg font-semibold text-gray-900 dark:text-white"
                      >
                        Pricing & Batch Details
                      </h4>
                      <p
                        class="text-sm text-purple-600 dark:text-purple-400 mt-1"
                      >
                        Advanced pricing tiers and inventory management
                      </p>
                    </div>
                  </div>
                  <div class="flex items-center">
                    <span
                      class="text-sm text-purple-600 dark:text-purple-400 mr-3"
                    >
                      View more details 3/3
                    </span>
                    <vue-feather
                      :type="showLevel3Fields ? 'chevron-up' : 'chevron-down'"
                      size="20"
                      class="text-purple-600 dark:text-purple-400 transition-transform duration-200"
                      :class="{ 'rotate-180': showLevel3Fields }"
                    />
                  </div>
                </div>
              </button>

              <div
                v-if="showLevel3Fields"
                class="px-6 pb-6 space-y-6 animate-fadeIn"
              >
                <!-- Group Pricing Section -->
                <div
                  class="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4 border border-blue-200 dark:border-blue-700"
                >
                  <h5
                    class="text-sm font-semibold text-blue-800 dark:text-blue-300 mb-3 flex items-center"
                  >
                    <vue-feather type="dollar-sign" size="16" class="mr-2" />
                    Group Pricing Tiers
                    <span class="text-xs text-blue-600 dark:text-blue-400 ml-2">
                      (Auto-filled from Sale Unit Price)
                    </span>
                  </h5>
                  <div
                    class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4"
                  >
                    <!-- Group1 Unit Price -->
                    <div>
                      <label
                        class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                      >
                        Group1 Unit Price
                      </label>
                      <input
                        v-model="formData.ebmItem.grpPrcL1"
                        type="text"
                        disabled
                        class="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-800 dark:text-white disabled:bg-gray-100 disabled:text-gray-500 dark:disabled:bg-gray-700 dark:disabled:text-gray-400 disabled:cursor-not-allowed"
                        :class="{
                          'border-red-500 ring-red-500':
                            hasFieldError('ebmItem.grpPrcL1'),
                        }"
                        placeholder="Auto-calculated"
                      />
                    </div>

                    <!-- Group2 Unit Price -->
                    <div>
                      <label
                        class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                      >
                        Group2 Unit Price
                      </label>
                      <input
                        v-model="formData.ebmItem.grpPrcL2"
                        type="text"
                        disabled
                        class="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-800 dark:text-white disabled:bg-gray-100 disabled:text-gray-500 dark:disabled:bg-gray-700 dark:disabled:text-gray-400 disabled:cursor-not-allowed"
                        :class="{
                          'border-red-500 ring-red-500':
                            hasFieldError('ebmItem.grpPrcL2'),
                        }"
                        placeholder="Auto-calculated"
                      />
                    </div>

                    <!-- Group3 Unit Price -->
                    <div>
                      <label
                        class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                      >
                        Group3 Unit Price
                      </label>
                      <input
                        v-model="formData.ebmItem.grpPrcL3"
                        type="text"
                        disabled
                        class="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-800 dark:text-white disabled:bg-gray-100 disabled:text-gray-500 dark:disabled:bg-gray-700 dark:disabled:text-gray-400 disabled:cursor-not-allowed"
                        :class="{
                          'border-red-500 ring-red-500':
                            hasFieldError('ebmItem.grpPrcL3'),
                        }"
                        placeholder="Auto-calculated"
                      />
                    </div>

                    <!-- Group4 Unit Price -->
                    <div>
                      <label
                        class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                      >
                        Group4 Unit Price
                      </label>
                      <input
                        v-model="formData.ebmItem.grpPrcL4"
                        type="text"
                        disabled
                        class="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-800 dark:text-white disabled:bg-gray-100 disabled:text-gray-500 dark:disabled:bg-gray-700 dark:disabled:text-gray-400 disabled:cursor-not-allowed"
                        :class="{
                          'border-red-500 ring-red-500':
                            hasFieldError('ebmItem.grpPrcL4'),
                        }"
                        placeholder="Auto-calculated"
                      />
                    </div>

                    <!-- Group5 Unit Price -->
                    <div>
                      <label
                        class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                      >
                        Group5 Unit Price
                      </label>
                      <input
                        v-model="formData.ebmItem.grpPrcL5"
                        type="text"
                        disabled
                        class="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-800 dark:text-white disabled:bg-gray-100 disabled:text-gray-500 dark:disabled:bg-gray-700 dark:disabled:text-gray-400 disabled:cursor-not-allowed"
                        :class="{
                          'border-red-500 ring-red-500':
                            hasFieldError('ebmItem.grpPrcL5'),
                        }"
                        placeholder="Auto-calculated"
                      />
                    </div>
                  </div>
                </div>

                <!-- Batch & Inventory Section -->
                <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <!-- Batch Number -->
                  <div>
                    <label
                      class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                    >
                      Batch Number
                    </label>
                    <input
                      v-model="formData.ebmItem.btchNo"
                      type="text"
                      maxlength="10"
                      class="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-800 dark:text-white transition-all duration-200"
                      :class="{
                        'border-red-500 ring-red-500':
                          hasFieldError('ebmItem.btchNo'),
                      }"
                      placeholder="Enter batch number"
                    />
                  </div>

                  <!-- Safety Quantity -->
                  <div>
                    <label
                      class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                    >
                      Safety Quantity
                    </label>
                    <input
                      v-model="formData.ebmItem.sftyQty"
                      type="text"
                      step="0.01"
                      class="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-800 dark:text-white transition-all duration-200"
                      :class="{
                        'border-red-500 ring-red-500':
                          hasFieldError('ebmItem.sftyQty'),
                      }"
                      placeholder="Enter safety quantity"
                    />
                  </div>

                  <!-- Additional Information -->
                  <div class="md:col-span-2">
                    <label
                      class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                    >
                      Additional Information
                    </label>
                    <input
                      v-model="formData.ebmItem.addInfo"
                      type="text"
                      maxlength="7"
                      class="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-800 dark:text-white transition-all duration-200"
                      :class="{
                        'border-red-500 ring-red-500':
                          hasFieldError('ebmItem.addInfo'),
                      }"
                      placeholder="Enter additional information"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Purchase Data Tab -->
          <div v-if="activeTab === 'purchase'" class="space-y-6">
            <!-- Level 1: Essential Overview -->
            <div
              class="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-xl p-6 border border-blue-200 dark:border-blue-700"
            >
              <div class="flex items-center justify-between mb-6">
                <div class="flex items-center">
                  <div
                    class="flex items-center justify-center w-8 h-8 bg-blue-600 text-white rounded-full text-sm font-semibold mr-3"
                  >
                    1
                  </div>
                  <h4
                    class="text-lg font-semibold text-gray-900 dark:text-white"
                  >
                    Essential Overview
                  </h4>
                </div>
                <div
                  class="text-sm text-blue-600 dark:text-blue-400 font-medium"
                >
                  Core purchase information
                </div>
              </div>

              <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                <!-- Quantity -->
                <div>
                  <label
                    class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                  >
                    Quantity *
                  </label>
                  <input
                    v-model="editableQuantity"
                    :disabled="true"
                    class="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-800 dark:text-white transition-all duration-200"
                    :class="{
                      'border-red-500 ring-red-500':
                        hasFieldError('ebmPurchase.qty'),
                    }"
                    placeholder="Enter quantity"
                  />
                </div>

                <!-- Purchase Unit Price -->
                <div class="relative">
                  <label
                    class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                    :class="{
                      'text-amber-600 dark:text-amber-400 animate-pulse':
                        !formData.ebmPurchase.prc ||
                        formData.ebmPurchase.prc <= 0,
                    }"
                  >
                    Purchase Unit Price *
                    <span
                      v-if="
                        !formData.ebmPurchase.prc ||
                        formData.ebmPurchase.prc <= 0
                      "
                      class="ml-2 inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200 animate-bounce"
                    >
                      ⚠️ Required
                    </span>
                  </label>
                  <div class="relative">
                    <input
                      v-model="formData.ebmPurchase.prc"
                      :disabled="isPurchaseDataDisabled"
                      class="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-800 dark:text-white transition-all duration-200"
                      :class="{
                        'border-red-500 ring-red-500':
                          hasFieldError('ebmPurchase.prc') ||
                          !formData.ebmPurchase.prc ||
                          formData.ebmPurchase.prc <= 0,
                        'ring-4 ring-amber-200 dark:ring-amber-800 animate-pulse':
                          !formData.ebmPurchase.prc ||
                          formData.ebmPurchase.prc <= 0,
                      }"
                      placeholder="Enter purchase unit price"
                    />
                    <!-- Attention indicator -->
                    <div
                      v-if="
                        !formData.ebmPurchase.prc ||
                        formData.ebmPurchase.prc <= 0
                      "
                      class="absolute -right-2 -top-2 w-6 h-6 bg-amber-500 rounded-full flex items-center justify-center animate-ping"
                    >
                      <span class="text-white text-xs font-bold">!</span>
                    </div>
                  </div>
                  <!-- Help text for missing data -->
                  <p
                    v-if="
                      !formData.ebmPurchase.prc || formData.ebmPurchase.prc <= 0
                    "
                    class="mt-2 text-sm text-amber-600 dark:text-amber-400 flex items-center"
                  >
                    <svg
                      class="w-4 h-4 mr-1"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fill-rule="evenodd"
                        d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                        clip-rule="evenodd"
                      ></path>
                    </svg>
                    Please enter the purchase unit price to calculate taxes and
                    totals
                  </p>
                </div>
              </div>
            </div>

            <!-- Level 2: Basic Purchase Details (Expandable) -->
            <div
              class="bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 rounded-xl border border-green-200 dark:border-green-700 overflow-hidden"
            >
              <button
                @click="showPurchaseLevel2Fields = !showPurchaseLevel2Fields"
                class="w-full p-6 text-left hover:bg-green-100 dark:hover:bg-green-800/30 transition-colors duration-200"
              >
                <div class="flex items-center justify-between">
                  <div class="flex items-center">
                    <div
                      class="flex items-center justify-center w-8 h-8 bg-green-600 text-white rounded-full text-sm font-semibold mr-3"
                    >
                      2
                    </div>
                    <div>
                      <h4
                        class="text-lg font-semibold text-gray-900 dark:text-white"
                      >
                        Basic Purchase Details
                      </h4>
                      <p
                        class="text-sm text-green-600 dark:text-green-400 mt-1"
                      >
                        Essential purchase information and calculations
                      </p>
                    </div>
                  </div>
                  <div class="flex items-center">
                    <span
                      class="text-sm text-green-600 dark:text-green-400 mr-3"
                    >
                      View more details 2/5
                    </span>
                    <vue-feather
                      :type="
                        showPurchaseLevel2Fields ? 'chevron-up' : 'chevron-down'
                      "
                      size="20"
                      class="text-green-600 dark:text-green-400 transition-transform duration-200"
                      :class="{ 'rotate-180': showPurchaseLevel2Fields }"
                    />
                  </div>
                </div>
              </button>

              <div
                v-if="showPurchaseLevel2Fields"
                class="px-6 pb-6 space-y-6 animate-fadeIn"
              >
                <!-- Locked Fields Section -->
                <div
                  class="bg-orange-50 dark:bg-orange-900/20 rounded-lg p-4 border border-orange-200 dark:border-orange-700"
                >
                  <h5
                    class="text-sm font-semibold text-orange-800 dark:text-orange-300 mb-3 flex items-center"
                  >
                    <vue-feather type="lock" size="16" class="mr-2" />
                    System Locked Fields
                  </h5>
                  <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <!-- TIN -->
                    <div>
                      <label
                        class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                      >
                        TIN *
                        <span
                          class="text-xs text-orange-600 dark:text-orange-400 ml-2"
                        >
                          (Locked - EBM settings data)
                        </span>
                      </label>
                      <input
                        v-model="formData.ebmPurchase.tin"
                        type="text"
                        maxlength="9"
                        :disabled="true"
                        class="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-800 dark:text-white disabled:bg-gray-100 disabled:text-gray-500 dark:disabled:bg-gray-700 dark:disabled:text-gray-400"
                        :class="{
                          'border-red-500 ring-red-500':
                            hasFieldError('ebmPurchase.tin'),
                        }"
                        placeholder="Auto-filled from settings"
                      />
                    </div>

                    <!-- Branch ID -->
                    <div>
                      <label
                        class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                      >
                        Branch ID *
                        <span
                          class="text-xs text-orange-600 dark:text-orange-400 ml-2"
                        >
                          (Locked - EBM settings data)
                        </span>
                      </label>
                      <input
                        v-model="formData.ebmPurchase.bhfId"
                        type="text"
                        maxlength="2"
                        :disabled="true"
                        class="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-800 dark:text-white disabled:bg-gray-100 disabled:text-gray-500 dark:disabled:bg-gray-700 dark:disabled:text-gray-400"
                        :class="{
                          'border-red-500 ring-red-500':
                            hasFieldError('ebmPurchase.bhfId'),
                        }"
                        placeholder="Auto-filled from settings"
                      />
                    </div>
                  </div>
                </div>

                <!-- Editable Fields Section -->
                <div
                  class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4"
                >
                  <!-- Invoice Number -->
                  <div>
                    <label
                      class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                    >
                      Invoice Number *
                      <span
                        v-if="isPurchaseDataDisabled"
                        class="text-xs text-orange-600 dark:text-orange-400 ml-2"
                      >
                        (Locked)
                      </span>
                    </label>
                    <input
                      v-model="formData.ebmPurchase.invcNo"
                      type="text"
                      :disabled="isPurchaseDataDisabled"
                      class="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-800 dark:text-white transition-all duration-200"
                      :class="{
                        'border-red-500 ring-red-500':
                          hasFieldError('ebmPurchase.invcNo'),
                      }"
                      placeholder="Enter invoice number"
                    />
                  </div>

                  <!-- Original Invoice Number -->
                  <div>
                    <label
                      class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                    >
                      Original Invoice Number *
                      <span
                        v-if="isPurchaseDataDisabled"
                        class="text-xs text-orange-600 dark:text-orange-400 ml-2"
                      >
                        (Locked)
                      </span>
                    </label>
                    <input
                      v-model="formData.ebmPurchase.orgInvcNo"
                      type="text"
                      :disabled="isPurchaseDataDisabled"
                      class="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-800 dark:text-white transition-all duration-200"
                      :class="{
                        'border-red-500 ring-red-500': hasFieldError(
                          'ebmPurchase.orgInvcNo'
                        ),
                      }"
                      placeholder="Enter original invoice number"
                    />
                  </div>

                  <!-- Registration Type Code -->
                  <div>
                    <EbmSelectDropdown
                      field-name="regTyCd"
                      v-model="formData.ebmPurchase.regTyCd"
                      label="Registration Type Code *"
                      placeholder="Search registration types..."
                      :disabled="isPurchaseDataDisabled"
                      :has-error="hasFieldError('ebmPurchase.regTyCd')"
                      :show-locked-message="true"
                      :is-locked="isPurchaseDataDisabled"
                    />
                  </div>

                  <!-- Purchase Type Code -->
                  <div>
                    <EbmSelectDropdown
                      field-name="pchsTyCd"
                      v-model="formData.ebmPurchase.pchsTyCd"
                      label="Purchase Type Code *"
                      placeholder="Search purchase types..."
                      :disabled="isPurchaseDataDisabled"
                      :has-error="hasFieldError('ebmPurchase.pchsTyCd')"
                      :show-locked-message="true"
                      :is-locked="isPurchaseDataDisabled"
                    />
                  </div>

                  <!-- Total Taxable Amount -->
                  <div>
                    <label
                      class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                    >
                      Total Taxable Amount *
                    </label>
                    <input
                      :value="calculatedTotTaxblAmt"
                      :disabled="true"
                      class="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 disabled:cursor-not-allowed"
                      placeholder="Calculated automatically"
                    />
                  </div>

                  <!-- Total Tax Amount -->
                  <div>
                    <label
                      class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                    >
                      Total Tax Amount *
                    </label>
                    <input
                      :value="calculatedTotTaxAmt"
                      :disabled="true"
                      class="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 disabled:cursor-not-allowed"
                      placeholder="Calculated automatically"
                    />
                  </div>
                </div>
              </div>
            </div>

            <!-- Level 3: Transaction & Status Info (Expandable) -->
            <div
              class="bg-gradient-to-r from-purple-50 to-violet-50 dark:from-purple-900/20 dark:to-violet-900/20 rounded-xl border border-purple-200 dark:border-purple-700 overflow-hidden"
            >
              <button
                @click="showPurchaseLevel3Fields = !showPurchaseLevel3Fields"
                class="w-full p-6 text-left hover:bg-purple-100 dark:hover:bg-purple-800/30 transition-colors duration-200"
              >
                <div class="flex items-center justify-between">
                  <div class="flex items-center">
                    <div
                      class="flex items-center justify-center w-8 h-8 bg-purple-600 text-white rounded-full text-sm font-semibold mr-3"
                    >
                      3
                    </div>
                    <div>
                      <h4
                        class="text-lg font-semibold text-gray-900 dark:text-white"
                      >
                        Transaction & Status Info
                      </h4>
                      <p
                        class="text-sm text-purple-600 dark:text-purple-400 mt-1"
                      >
                        Transaction totals and status information
                      </p>
                    </div>
                  </div>
                  <div class="flex items-center">
                    <span
                      class="text-sm text-purple-600 dark:text-purple-400 mr-3"
                    >
                      View more details 3/5
                    </span>
                    <vue-feather
                      :type="
                        showPurchaseLevel3Fields ? 'chevron-up' : 'chevron-down'
                      "
                      size="20"
                      class="text-purple-600 dark:text-purple-400 transition-transform duration-200"
                      :class="{ 'rotate-180': showPurchaseLevel3Fields }"
                    />
                  </div>
                </div>
              </button>

              <div
                v-if="showPurchaseLevel3Fields"
                class="px-6 pb-6 space-y-6 animate-fadeIn"
              >
                <div
                  class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4"
                >
                  <!-- Total Amount -->
                  <div>
                    <label
                      class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                    >
                      Total Amount *
                    </label>
                    <input
                      :value="calculatedTotAmt"
                      :disabled="true"
                      class="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 disabled:cursor-not-allowed"
                      placeholder="Calculated automatically"
                    />
                  </div>

                  <!-- Total Item Count -->
                  <div>
                    <label
                      class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                    >
                      Total Item Count *
                      <span
                        class="text-xs text-gray-500 dark:text-gray-400 ml-2"
                      >
                        (Fixed at 1)
                      </span>
                    </label>
                    <input
                      v-model="formData.ebmPurchase.totItemCnt"
                      :disabled="true"
                      class="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 disabled:cursor-not-allowed"
                      placeholder="Fixed at 1"
                    />
                  </div>

                  <!-- Receipt Type Code -->
                  <div>
                    <EbmSelectDropdown
                      field-name="rctTyCd"
                      v-model="formData.ebmPurchase.rcptTyCd"
                      label="Receipt Type Code *"
                      placeholder="Search receipt types..."
                      :disabled="isPurchaseDataDisabled"
                      :has-error="hasFieldError('ebmPurchase.rcptTyCd')"
                      :show-locked-message="true"
                      :is-locked="isPurchaseDataDisabled"
                    />
                  </div>

                  <!-- Payment Type Code -->
                  <div>
                    <EbmSelectDropdown
                      field-name="pmtTyCd"
                      v-model="formData.ebmPurchase.pmtTyCd"
                      label="Payment Type Code *"
                      placeholder="Search payment types..."
                      :disabled="isPurchaseDataDisabled"
                      :has-error="hasFieldError('ebmPurchase.pmtTyCd')"
                      :show-locked-message="true"
                      :is-locked="isPurchaseDataDisabled"
                    />
                  </div>

                  <!-- Purchase Status Code -->
                  <div>
                    <EbmSelectDropdown
                      field-name="pchsSttsCd"
                      v-model="formData.ebmPurchase.pchsSttsCd"
                      label="Purchase Status Code *"
                      placeholder="Search purchase statuses..."
                      :disabled="isPurchaseDataDisabled"
                      :has-error="hasFieldError('ebmPurchase.pchsSttsCd')"
                      :show-locked-message="true"
                      :is-locked="isPurchaseDataDisabled"
                    />
                  </div>

                  <!-- Purchase Date -->
                  <div>
                    <label
                      class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                    >
                      Purchase Date *
                      <span
                        v-if="isPurchaseDataDisabled"
                        class="text-xs text-orange-600 dark:text-orange-400 ml-2"
                      >
                        (Locked)
                      </span>
                    </label>
                    <hs-date-input
                      v-model="pchsDtDisplay"
                      placeholder="YYYY-MM-DD"
                      :disabled="isPurchaseDataDisabled"
                      class="w-full"
                      :error="hasFieldError('ebmPurchase.pchsDt') ? 'Invalid purchase date' : ''"
                      :class="{
                        'ring-2 ring-red-500':
                          hasFieldError('ebmPurchase.pchsDt'),
                      }"
                    />
                  </div>
                </div>
              </div>
            </div>

            <!-- Level 4: Additional Purchase Details & Advanced Tax Details (Expandable) -->
            <div
              class="bg-gradient-to-r from-orange-50 to-red-50 dark:from-orange-900/20 dark:to-red-900/20 rounded-xl border border-orange-200 dark:border-orange-700 overflow-hidden"
            >
              <button
                @click="showPurchaseLevel4Fields = !showPurchaseLevel4Fields"
                class="w-full p-6 text-left hover:bg-orange-100 dark:hover:bg-orange-800/30 transition-colors duration-200"
              >
                <div class="flex items-center justify-between">
                  <div class="flex items-center">
                    <div
                      class="flex items-center justify-center w-8 h-8 bg-orange-600 text-white rounded-full text-sm font-semibold mr-3"
                    >
                      4
                    </div>
                    <div>
                      <h4
                        class="text-lg font-semibold text-gray-900 dark:text-white"
                      >
                        Supplier Purchase Details
                      </h4>
                      <p
                        class="text-sm text-orange-600 dark:text-orange-400 mt-1"
                      >
                        Supplier, purchase and registrant details
                      </p>
                    </div>
                  </div>
                  <div class="flex items-center">
                    <span
                      class="text-sm text-orange-600 dark:text-orange-400 mr-3"
                    >
                      View more details 4/5
                    </span>
                    <vue-feather
                      :type="
                        showPurchaseLevel4Fields ? 'chevron-up' : 'chevron-down'
                      "
                      size="20"
                      class="text-orange-600 dark:text-orange-400 transition-transform duration-200"
                      :class="{ 'rotate-180': showPurchaseLevel4Fields }"
                    />
                  </div>
                </div>
              </button>

              <div
                v-if="showPurchaseLevel4Fields"
                class="px-6 pb-6 space-y-6 animate-fadeIn"
              >
                <!-- Supplier Information Section -->
                <div
                  class="bg-green-50 dark:bg-green-900/20 rounded-lg p-4 border border-green-200 dark:border-green-700"
                >
                  <h5
                    class="text-sm font-semibold text-green-800 dark:text-green-300 mb-3 flex items-center"
                  >
                    <vue-feather type="truck" size="16" class="mr-2" />
                    Supplier Information
                  </h5>
                  <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <!-- Supplier TIN -->
                    <div>
                      <label
                        class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                      >
                        Supplier TIN *
                        <span
                          v-if="isPurchaseDataDisabled"
                          class="text-xs text-orange-600 dark:text-orange-400 ml-2"
                        >
                          (Locked)
                        </span>
                      </label>
                      <input
                        v-model="formData.ebmPurchase.spplrTin"
                        type="text"
                        minlength="5"
                        :disabled="isPurchaseDataDisabled"
                        class="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-800 dark:text-white transition-all duration-200"
                        :class="{
                          'border-red-500 ring-red-500': hasFieldError(
                            'ebmPurchase.spplrTin'
                          ),
                        }"
                        placeholder="Enter supplier TIN"
                      />
                    </div>

                    <!-- Supplier Branch ID -->
                    <div>
                      <label
                        class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                      >
                        Supplier Branch ID *
                        <span
                          v-if="isPurchaseDataDisabled"
                          class="text-xs text-orange-600 dark:text-orange-400 ml-2"
                        >
                          (Locked)
                        </span>
                      </label>
                      <input
                        v-model="formData.ebmPurchase.spplrBhfId"
                        type="text"
                        maxlength="2"
                        :disabled="isPurchaseDataDisabled"
                        class="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-800 dark:text-white transition-all duration-200"
                        :class="{
                          'border-red-500 ring-red-500': hasFieldError(
                            'ebmPurchase.spplrBhfId'
                          ),
                        }"
                        placeholder="Enter supplier branch ID"
                      />
                    </div>

                    <!-- Supplier Name -->
                    <div>
                      <label
                        class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                      >
                        Supplier Name *
                        <span
                          v-if="isPurchaseDataDisabled"
                          class="text-xs text-orange-600 dark:text-orange-400 ml-2"
                        >
                          (Locked)
                        </span>
                      </label>
                      <input
                        v-model="formData.ebmPurchase.spplrNm"
                        type="text"
                        maxlength="60"
                        :disabled="isPurchaseDataDisabled"
                        class="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-800 dark:text-white transition-all duration-200"
                        :class="{
                          'border-red-500 ring-red-500': hasFieldError(
                            'ebmPurchase.spplrNm'
                          ),
                        }"
                        placeholder="Enter supplier name"
                      />
                    </div>

                    <!-- Supplier Invoice Number -->
                    <div>
                      <label
                        class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                      >
                        Supplier Invoice Number *
                        <span
                          v-if="isPurchaseDataDisabled"
                          class="text-xs text-orange-600 dark:text-orange-400 ml-2"
                        >
                          (Locked)
                        </span>
                      </label>
                      <input
                        v-model="formData.ebmPurchase.spplrInvcNo"
                        type="text"
                        :disabled="isPurchaseDataDisabled"
                        class="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-800 dark:text-white transition-all duration-200"
                        :class="{
                          'border-red-500 ring-red-500': hasFieldError(
                            'ebmPurchase.spplrInvcNo'
                          ),
                        }"
                        placeholder="Enter supplier invoice number"
                      />
                    </div>
                  </div>
                </div>
                <!-- Registrant & Modifier Section -->
                <div
                  class="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4 border border-blue-200 dark:border-blue-700"
                >
                  <h5
                    class="text-sm font-semibold text-blue-800 dark:text-blue-300 mb-3 flex items-center"
                  >
                    <vue-feather type="user" size="16" class="mr-2" />
                    Registrant & Modifier Information
                  </h5>
                  <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <!-- Registrant ID -->
                    <div>
                      <label
                        class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                      >
                        Registrant ID *
                        <span
                          v-if="isPurchaseDataDisabled"
                          class="text-xs text-orange-600 dark:text-orange-400 ml-2"
                        >
                          (Locked)
                        </span>
                      </label>
                      <input
                        v-model="formData.ebmPurchase.regrId"
                        type="text"
                        maxlength="20"
                        :disabled="isPurchaseDataDisabled"
                        class="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-800 dark:text-white transition-all duration-200"
                        :class="{
                          'border-red-500 ring-red-500':
                            hasFieldError('ebmPurchase.regrId'),
                        }"
                        placeholder="Enter registrant ID"
                      />
                    </div>

                    <!-- Registrant Name -->
                    <div>
                      <label
                        class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                      >
                        Registrant Name *
                        <span
                          v-if="isPurchaseDataDisabled"
                          class="text-xs text-orange-600 dark:text-orange-400 ml-2"
                        >
                          (Locked)
                        </span>
                      </label>
                      <input
                        v-model="formData.ebmPurchase.regrNm"
                        type="text"
                        maxlength="60"
                        :disabled="isPurchaseDataDisabled"
                        class="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-800 dark:text-white transition-all duration-200"
                        :class="{
                          'border-red-500 ring-red-500':
                            hasFieldError('ebmPurchase.regrNm'),
                        }"
                        placeholder="Enter registrant name"
                      />
                    </div>

                    <!-- Modifier Name -->
                    <div>
                      <label
                        class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                      >
                        Modifier Name *
                        <span
                          v-if="isPurchaseDataDisabled"
                          class="text-xs text-orange-600 dark:text-orange-400 ml-2"
                        >
                          (Locked)
                        </span>
                      </label>
                      <input
                        v-model="formData.ebmPurchase.modrNm"
                        type="text"
                        maxlength="60"
                        :disabled="isPurchaseDataDisabled"
                        class="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-800 dark:text-white transition-all duration-200"
                        :class="{
                          'border-red-500 ring-red-500':
                            hasFieldError('ebmPurchase.modrNm'),
                        }"
                        placeholder="Enter modifier name"
                      />
                    </div>

                    <!-- Modifier ID -->
                    <div>
                      <label
                        class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                      >
                        Modifier ID *
                        <span
                          v-if="isPurchaseDataDisabled"
                          class="text-xs text-orange-600 dark:text-orange-400 ml-2"
                        >
                          (Locked)
                        </span>
                      </label>
                      <input
                        v-model="formData.ebmPurchase.modrId"
                        type="text"
                        maxlength="20"
                        :disabled="isPurchaseDataDisabled"
                        class="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-800 dark:text-white transition-all duration-200"
                        :class="{
                          'border-red-500 ring-red-500':
                            hasFieldError('ebmPurchase.modrId'),
                        }"
                        placeholder="Enter modifier ID"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Level 5: Tax Breakdown (Expandable) -->
            <div
              class="bg-gradient-to-r from-indigo-50 to-blue-50 dark:from-indigo-900/20 dark:to-blue-900/20 rounded-xl border border-indigo-200 dark:border-indigo-700 overflow-hidden"
            >
              <button
                @click="showPurchaseLevel5Fields = !showPurchaseLevel5Fields"
                class="w-full p-6 text-left hover:bg-indigo-100 dark:hover:bg-indigo-800/30 transition-colors duration-200"
              >
                <div class="flex items-center justify-between">
                  <div class="flex items-center">
                    <div
                      class="flex items-center justify-center w-8 h-8 bg-indigo-600 text-white rounded-full text-sm font-semibold mr-3"
                    >
                      5
                    </div>
                    <div>
                      <h4
                        class="text-lg font-semibold text-gray-900 dark:text-white"
                      >
                        Tax Breakdown
                      </h4>
                      <p
                        class="text-sm text-indigo-600 dark:text-indigo-400 mt-1"
                      >
                        Detailed tax calculations and breakdowns
                      </p>
                    </div>
                  </div>
                  <div class="flex items-center">
                    <span
                      class="text-sm text-indigo-600 dark:text-indigo-400 mr-3"
                    >
                      View more details 5/5
                    </span>
                    <vue-feather
                      :type="
                        showPurchaseLevel5Fields ? 'chevron-up' : 'chevron-down'
                      "
                      size="20"
                      class="text-indigo-600 dark:text-indigo-400 transition-transform duration-200"
                      :class="{ 'rotate-180': showPurchaseLevel5Fields }"
                    />
                  </div>
                </div>
              </button>

              <div
                v-if="showPurchaseLevel5Fields"
                class="px-6 pb-6 space-y-6 animate-fadeIn"
              >
                <!-- Taxable Amounts Section -->
                <div
                  class="bg-purple-50 dark:bg-purple-900/20 rounded-lg p-4 border border-purple-200 dark:border-purple-700"
                >
                  <h5
                    class="text-sm font-semibold text-purple-800 dark:text-purple-300 mb-3 flex items-center"
                  >
                    <vue-feather type="hash" size="16" class="mr-2" />
                    Taxable Amounts
                  </h5>
                  <div
                    class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4"
                  >
                    <!-- Taxable Amount A -->
                    <div>
                      <label
                        class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                      >
                        Taxable Amount A *
                      </label>
                      <input
                        v-model="formData.ebmPurchase.taxblAmtA"
                        type="text"
                        class="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-800 dark:text-white transition-all duration-200"
                        :class="{
                          'border-red-500 ring-red-500': hasFieldError(
                            'ebmPurchase.taxblAmtA'
                          ),
                        }"
                        placeholder="Enter taxable amount A"
                      />
                    </div>

                    <!-- Taxable Amount B -->
                    <div>
                      <label
                        class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                      >
                        Taxable Amount B *
                      </label>
                      <input
                        v-model="formData.ebmPurchase.taxblAmtB"
                        type="text"
                        @input="trackUserEdit('taxblAmtB')"
                        class="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-800 dark:text-white transition-all duration-200"
                        :class="{
                          'border-red-500 ring-red-500': hasFieldError(
                            'ebmPurchase.taxblAmtB'
                          ),
                        }"
                        placeholder="Enter taxable amount B"
                      />
                    </div>

                    <!-- Taxable Amount C -->
                    <div>
                      <label
                        class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                      >
                        Taxable Amount C *
                      </label>
                      <input
                        v-model="formData.ebmPurchase.taxblAmtC"
                        type="text"
                        class="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-800 dark:text-white transition-all duration-200"
                        :class="{
                          'border-red-500 ring-red-500': hasFieldError(
                            'ebmPurchase.taxblAmtC'
                          ),
                        }"
                        placeholder="Enter taxable amount C"
                      />
                    </div>

                    <!-- Taxable Amount D -->
                    <div>
                      <label
                        class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                      >
                        Taxable Amount D *
                      </label>
                      <input
                        v-model="formData.ebmPurchase.taxblAmtD"
                        type="text"
                        class="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-800 dark:text-white transition-all duration-200"
                        :class="{
                          'border-red-500 ring-red-500': hasFieldError(
                            'ebmPurchase.taxblAmtD'
                          ),
                        }"
                        placeholder="Enter taxable amount D"
                      />
                    </div>
                  </div>
                </div>

                <!-- Tax Rates Section -->
                <div
                  class="bg-green-50 dark:bg-green-900/20 rounded-lg p-4 border border-green-200 dark:border-green-700"
                >
                  <h5
                    class="text-sm font-semibold text-green-800 dark:text-green-300 mb-3 flex items-center"
                  >
                    <vue-feather type="percent" size="16" class="mr-2" />
                    Tax Rates
                  </h5>
                  <div
                    class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4"
                  >
                    <!-- Tax Rate A -->
                    <div>
                      <label
                        class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                      >
                        Tax Rate A *
                      </label>
                      <input
                        v-model="formData.ebmPurchase.taxRtA"
                        type="text"
                        class="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-800 dark:text-white transition-all duration-200"
                        :class="{
                          'border-red-500 ring-red-500':
                            hasFieldError('ebmPurchase.taxRtA'),
                        }"
                        placeholder="Enter tax rate A"
                      />
                    </div>

                    <!-- Tax Rate B -->
                    <div>
                      <label
                        class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                      >
                        Tax Rate B *
                      </label>
                      <input
                        v-model="formData.ebmPurchase.taxRtB"
                        type="text"
                        class="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-800 dark:text-white transition-all duration-200"
                        :class="{
                          'border-red-500 ring-red-500':
                            hasFieldError('ebmPurchase.taxRtB'),
                        }"
                        placeholder="Enter tax rate B"
                      />
                    </div>

                    <!-- Tax Rate C -->
                    <div>
                      <label
                        class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                      >
                        Tax Rate C *
                      </label>
                      <input
                        v-model="formData.ebmPurchase.taxRtC"
                        type="text"
                        class="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-800 dark:text-white transition-all duration-200"
                        :class="{
                          'border-red-500 ring-red-500':
                            hasFieldError('ebmPurchase.taxRtC'),
                        }"
                        placeholder="Enter tax rate C"
                      />
                    </div>

                    <!-- Tax Rate D -->
                    <div>
                      <label
                        class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                      >
                        Tax Rate D *
                      </label>
                      <input
                        v-model="formData.ebmPurchase.taxRtD"
                        type="text"
                        class="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-800 dark:text-white transition-all duration-200"
                        :class="{
                          'border-red-500 ring-red-500':
                            hasFieldError('ebmPurchase.taxRtD'),
                        }"
                        placeholder="Enter tax rate D"
                      />
                    </div>
                  </div>
                </div>

                <!-- Tax Amounts Section -->
                <div
                  class="bg-red-50 dark:bg-red-900/20 rounded-lg p-4 border border-red-200 dark:border-red-700"
                >
                  <h5
                    class="text-sm font-semibold text-red-800 dark:text-red-300 mb-3 flex items-center"
                  >
                    <vue-feather type="dollar-sign" size="16" class="mr-2" />
                    Tax Amounts
                  </h5>
                  <div
                    class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4"
                  >
                    <!-- Tax Amount A -->
                    <div>
                      <label
                        class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                      >
                        Tax Amount A *
                      </label>
                      <input
                        v-model="formData.ebmPurchase.taxAmtA"
                        type="text"
                        class="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-800 dark:text-white transition-all duration-200"
                        :class="{
                          'border-red-500 ring-red-500': hasFieldError(
                            'ebmPurchase.taxAmtA'
                          ),
                        }"
                        placeholder="Enter tax amount A"
                      />
                    </div>

                    <!-- Tax Amount B -->
                    <div>
                      <label
                        class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                      >
                        Tax Amount B *
                      </label>
                      <input
                        v-model="formData.ebmPurchase.taxAmtB"
                        type="text"
                        @input="trackUserEdit('taxAmtB')"
                        class="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-800 dark:text-white transition-all duration-200"
                        :class="{
                          'border-red-500 ring-red-500': hasFieldError(
                            'ebmPurchase.taxAmtB'
                          ),
                        }"
                        placeholder="Enter tax amount B"
                      />
                    </div>

                    <!-- Tax Amount C -->
                    <div>
                      <label
                        class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                      >
                        Tax Amount C *
                      </label>
                      <input
                        v-model="formData.ebmPurchase.taxAmtC"
                        type="text"
                        class="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-800 dark:text-white transition-all duration-200"
                        :class="{
                          'border-red-500 ring-red-500': hasFieldError(
                            'ebmPurchase.taxAmtC'
                          ),
                        }"
                        placeholder="Enter tax amount C"
                      />
                    </div>

                    <!-- Tax Amount D -->
                    <div>
                      <label
                        class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                      >
                        Tax Amount D *
                      </label>
                      <input
                        v-model="formData.ebmPurchase.taxAmtD"
                        type="text"
                        class="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-800 dark:text-white transition-all duration-200"
                        :class="{
                          'border-red-500 ring-red-500': hasFieldError(
                            'ebmPurchase.taxAmtD'
                          ),
                        }"
                        placeholder="Enter tax amount D"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- EBM Preview Tab -->
          <div v-if="activeTab === 'preview'" class="space-y-6">
            <!-- Section 1: Product Data (Collapsible) -->
            <div
              class="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-xl border border-blue-200 dark:border-blue-700 overflow-hidden"
            >
              <button
                @click="showPreviewProductDetails = !showPreviewProductDetails"
                class="w-full p-6 text-left hover:bg-blue-100 dark:hover:bg-blue-800/30 transition-colors duration-200"
              >
                <div class="flex items-center justify-between">
                  <div class="flex items-center">
                    <div
                      class="flex items-center justify-center w-8 h-8 bg-blue-600 text-white rounded-full text-sm font-semibold mr-3"
                    >
                      <vue-feather type="package" size="16" />
                    </div>
                    <div>
                      <h4
                        class="text-lg font-semibold text-gray-900 dark:text-white"
                      >
                        Product Data
                      </h4>
                      <p class="text-sm text-blue-600 dark:text-blue-400 mt-1">
                        Essential product information
                      </p>
                    </div>
                  </div>
                  <div class="flex items-center">
                    <span class="text-sm text-blue-600 dark:text-blue-400 mr-3">
                      {{
                        showPreviewProductDetails
                          ? "Hide details"
                          : "View more product details"
                      }}
                    </span>
                    <vue-feather
                      :type="
                        showPreviewProductDetails
                          ? 'chevron-up'
                          : 'chevron-down'
                      "
                      size="20"
                      class="text-blue-600 dark:text-blue-400 transition-transform duration-200"
                      :class="{ 'rotate-180': showPreviewProductDetails }"
                    />
                  </div>
                </div>
              </button>

              <!-- Level 1 Essentials (Always Visible) -->
              <div class="px-6 pb-6">
                <div
                  class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4"
                >
                  <div>
                    <label
                      class="text-sm font-medium text-gray-500 dark:text-gray-400"
                      >Item Name</label
                    >
                    <p
                      class="text-sm text-gray-900 dark:text-white font-medium"
                    >
                      {{ transformedEbmItemData.itemNm || "Not specified" }}
                    </p>
                  </div>
                  <div>
                    <label
                      class="text-sm font-medium text-gray-500 dark:text-gray-400"
                      >Item Code</label
                    >
                    <p
                      class="text-sm text-gray-900 dark:text-white font-medium"
                    >
                      {{ transformedEbmItemData.itemCd || "Not specified" }}
                    </p>
                  </div>
                  <div>
                    <label
                      class="text-sm font-medium text-gray-500 dark:text-gray-400"
                      >Origin Country Code</label
                    >
                    <p
                      class="text-sm text-gray-900 dark:text-white font-medium"
                    >
                      {{ transformedEbmItemData.orgnNatCd || "RW" }}
                    </p>
                  </div>
                  <div>
                    <label
                      class="text-sm font-medium text-gray-500 dark:text-gray-400"
                      >Quantity Unit Code</label
                    >
                    <p
                      class="text-sm text-gray-900 dark:text-white font-medium"
                    >
                      {{
                        getQuantityUnitLabel(transformedEbmItemData.qtyUnitCd)
                      }}
                    </p>
                  </div>
                  <div>
                    <label
                      class="text-sm font-medium text-gray-500 dark:text-gray-400"
                      >Taxation Type Code</label
                    >
                    <p
                      class="text-sm text-gray-900 dark:text-white font-medium"
                    >
                      {{ getTaxTypeLabel(transformedEbmItemData.taxTyCd) }}
                    </p>
                  </div>
                  <div>
                    <label
                      class="text-sm font-medium text-gray-500 dark:text-gray-400"
                      >Sale Unit Price</label
                    >
                    <p
                      class="text-sm text-gray-900 dark:text-white font-medium"
                    >
                      {{
                        transformedEbmItemData.dftPrc
                          ? `${formatDisplayNumber(
                              transformedEbmItemData.dftPrc
                            )} RWF`
                          : "Not specified"
                      }}
                    </p>
                  </div>
                </div>
              </div>

              <!-- Expanded Details -->
              <div
                v-if="showPreviewProductDetails"
                class="px-6 pb-6 space-y-6 animate-fadeIn border-t border-blue-200 dark:border-blue-700"
              >
                <!-- Level 2: Key Metadata -->
                <div
                  class="bg-green-50 dark:bg-green-900/20 rounded-lg p-4 border border-green-200 dark:border-green-700"
                >
                  <h5
                    class="text-sm font-semibold text-green-800 dark:text-green-300 mb-3 flex items-center"
                  >
                    <vue-feather type="info" size="16" class="mr-2" />
                    Key Product Metadata
                  </h5>
                  <div
                    class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4"
                  >
                    <div>
                      <label
                        class="text-sm font-medium text-gray-500 dark:text-gray-400"
                        >TIN</label
                      >
                      <p
                        class="text-sm text-gray-900 dark:text-white font-medium"
                      >
                        {{ transformedEbmItemData.tin || "Not specified" }}
                      </p>
                    </div>
                    <div>
                      <label
                        class="text-sm font-medium text-gray-500 dark:text-gray-400"
                        >Branch ID</label
                      >
                      <p
                        class="text-sm text-gray-900 dark:text-white font-medium"
                      >
                        {{ transformedEbmItemData.bhfId || "Not specified" }}
                      </p>
                    </div>
                    <div>
                      <label
                        class="text-sm font-medium text-gray-500 dark:text-gray-400"
                        >Item Type Code</label
                      >
                      <p
                        class="text-sm text-gray-900 dark:text-white font-medium"
                      >
                        {{ getItemTypeLabel(transformedEbmItemData.itemTyCd) }}
                      </p>
                    </div>
                    <div>
                      <label
                        class="text-sm font-medium text-gray-500 dark:text-gray-400"
                        >Item Classification Code</label
                      >
                      <p
                        class="text-sm text-gray-900 dark:text-white font-medium"
                      >
                        {{
                          getItemClassificationLabel(
                            transformedEbmItemData.itemClsCd
                          )
                        }}
                      </p>
                    </div>
                    <div>
                      <label
                        class="text-sm font-medium text-gray-500 dark:text-gray-400"
                        >Barcode</label
                      >
                      <p
                        class="text-sm text-gray-900 dark:text-white font-medium"
                      >
                        {{ transformedEbmItemData.bcd || "Not specified" }}
                      </p>
                    </div>
                    <div>
                      <label
                        class="text-sm font-medium text-gray-500 dark:text-gray-400"
                        >Insurance Applicability</label
                      >
                      <p
                        class="text-sm text-gray-900 dark:text-white font-medium"
                      >
                        {{
                          transformedEbmItemData.isrcAplcbYn === "Y"
                            ? "Yes"
                            : "No"
                        }}
                      </p>
                    </div>
                  </div>
                </div>

                <!-- Level 3: Pricing & Batch -->
                <div
                  class="bg-purple-50 dark:bg-purple-900/20 rounded-lg p-4 border border-purple-200 dark:border-purple-700"
                >
                  <h5
                    class="text-sm font-semibold text-purple-800 dark:text-purple-300 mb-3 flex items-center"
                  >
                    <vue-feather type="dollar-sign" size="16" class="mr-2" />
                    Pricing & Batch Details
                  </h5>
                  <div
                    class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4"
                  >
                    <div>
                      <label
                        class="text-sm font-medium text-gray-500 dark:text-gray-400"
                        >Batch Number</label
                      >
                      <p
                        class="text-sm text-gray-900 dark:text-white font-medium"
                      >
                        {{ transformedEbmItemData.btchNo || "Not specified" }}
                      </p>
                    </div>
                    <div>
                      <label
                        class="text-sm font-medium text-gray-500 dark:text-gray-400"
                        >Safety Quantity</label
                      >
                      <p
                        class="text-sm text-gray-900 dark:text-white font-medium"
                      >
                        {{ transformedEbmItemData.sftyQty || "Not specified" }}
                      </p>
                    </div>
                    <div>
                      <label
                        class="text-sm font-medium text-gray-500 dark:text-gray-400"
                        >Additional Information</label
                      >
                      <p
                        class="text-sm text-gray-900 dark:text-white font-medium"
                      >
                        {{ transformedEbmItemData.addInfo || "Not specified" }}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Section 2: Purchase Data (Collapsible) -->
            <div
              class="bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 rounded-xl border border-green-200 dark:border-green-700 overflow-hidden"
            >
              <button
                @click="
                  showPreviewPurchaseDetails = !showPreviewPurchaseDetails
                "
                class="w-full p-6 text-left hover:bg-green-100 dark:hover:bg-green-800/30 transition-colors duration-200"
              >
                <div class="flex items-center justify-between">
                  <div class="flex items-center">
                    <div
                      class="flex items-center justify-center w-8 h-8 bg-green-600 text-white rounded-full text-sm font-semibold mr-3"
                    >
                      <vue-feather type="shopping-cart" size="16" />
                    </div>
                    <div>
                      <h4
                        class="text-lg font-semibold text-gray-900 dark:text-white"
                      >
                        Purchase Data
                      </h4>
                      <p
                        class="text-sm text-green-600 dark:text-green-400 mt-1"
                      >
                        Essential purchase information
                      </p>
                    </div>
                  </div>
                  <div class="flex items-center">
                    <span
                      class="text-sm text-green-600 dark:text-green-400 mr-3"
                    >
                      {{
                        showPreviewPurchaseDetails
                          ? "Hide details"
                          : "View more purchase details"
                      }}
                    </span>
                    <vue-feather
                      :type="
                        showPreviewPurchaseDetails
                          ? 'chevron-up'
                          : 'chevron-down'
                      "
                      size="20"
                      class="text-green-600 dark:text-green-400 transition-transform duration-200"
                      :class="{ 'rotate-180': showPreviewPurchaseDetails }"
                    />
                  </div>
                </div>
              </button>

              <!-- Level 1 Essentials (Always Visible) -->
              <div class="px-6 pb-6">
                <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label
                      class="text-sm font-medium text-gray-500 dark:text-gray-400"
                      >Quantity</label
                    >
                    <p
                      class="text-sm text-gray-900 dark:text-white font-medium"
                    >
                      {{
                        transformedEbmPurchaseData.qty
                          ? formatDisplayNumber(transformedEbmPurchaseData.qty)
                          : "Not specified"
                      }}
                    </p>
                  </div>
                  <div>
                    <label
                      class="text-sm font-medium text-gray-500 dark:text-gray-400"
                      >Purchase Unit Price</label
                    >
                    <p
                      class="text-sm text-gray-900 dark:text-white font-medium"
                    >
                      {{
                        transformedEbmPurchaseData.prc
                          ? `${formatDisplayNumber(
                              transformedEbmPurchaseData.prc
                            )} RWF`
                          : "Not specified"
                      }}
                    </p>
                  </div>
                </div>
              </div>

              <!-- Expanded Details -->
              <div
                v-if="showPreviewPurchaseDetails"
                class="px-6 pb-6 space-y-6 animate-fadeIn border-t border-green-200 dark:border-green-700"
              >
                <!-- Level 2: Basic Purchase Details -->
                <div
                  class="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4 border border-blue-200 dark:border-blue-700"
                >
                  <h5
                    class="text-sm font-semibold text-blue-800 dark:text-blue-300 mb-3 flex items-center"
                  >
                    <vue-feather type="info" size="16" class="mr-2" />
                    Basic Purchase Details
                  </h5>
                  <div
                    class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4"
                  >
                    <div>
                      <label
                        class="text-sm font-medium text-gray-500 dark:text-gray-400"
                        >TIN</label
                      >
                      <p
                        class="text-sm text-gray-900 dark:text-white font-medium"
                      >
                        {{ transformedEbmPurchaseData.tin || "Not specified" }}
                      </p>
                    </div>
                    <div>
                      <label
                        class="text-sm font-medium text-gray-500 dark:text-gray-400"
                        >Branch ID</label
                      >
                      <p
                        class="text-sm text-gray-900 dark:text-white font-medium"
                      >
                        {{
                          transformedEbmPurchaseData.bhfId || "Not specified"
                        }}
                      </p>
                    </div>
                    <div>
                      <label
                        class="text-sm font-medium text-gray-500 dark:text-gray-400"
                        >Invoice Number</label
                      >
                      <p
                        class="text-sm text-gray-900 dark:text-white font-medium"
                      >
                        {{
                          transformedEbmPurchaseData.invcNo || "Not specified"
                        }}
                      </p>
                    </div>
                    <div>
                      <label
                        class="text-sm font-medium text-gray-500 dark:text-gray-400"
                        >Original Invoice Number</label
                      >
                      <p
                        class="text-sm text-gray-900 dark:text-white font-medium"
                      >
                        {{
                          transformedEbmPurchaseData.orgInvcNo ||
                          "Not specified"
                        }}
                      </p>
                    </div>
                    <div>
                      <label
                        class="text-sm font-medium text-gray-500 dark:text-gray-400"
                        >Registration Type Code</label
                      >
                      <p
                        class="text-sm text-gray-900 dark:text-white font-medium"
                      >
                        {{
                          transformedEbmPurchaseData.regTyCd || "Not specified"
                        }}
                      </p>
                    </div>
                    <div>
                      <label
                        class="text-sm font-medium text-gray-500 dark:text-gray-400"
                        >Purchase Type Code</label
                      >
                      <p
                        class="text-sm text-gray-900 dark:text-white font-medium"
                      >
                        {{
                          transformedEbmPurchaseData.pchsTyCd || "Not specified"
                        }}
                      </p>
                    </div>
                  </div>
                </div>

                <!-- Level 3: Transaction & Status -->
                <div
                  class="bg-purple-50 dark:bg-purple-900/20 rounded-lg p-4 border border-purple-200 dark:border-purple-700"
                >
                  <h5
                    class="text-sm font-semibold text-purple-800 dark:text-purple-300 mb-3 flex items-center"
                  >
                    <vue-feather type="dollar-sign" size="16" class="mr-2" />
                    Transaction & Status Info
                  </h5>
                  <div
                    class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4"
                  >
                    <div>
                      <label
                        class="text-sm font-medium text-gray-500 dark:text-gray-400"
                        >Total Amount</label
                      >
                      <p
                        class="text-sm text-gray-900 dark:text-white font-medium"
                      >
                        {{
                          calculatedTotAmt
                            ? `${calculatedTotAmt} RWF`
                            : "Not calculated"
                        }}
                      </p>
                    </div>
                    <div>
                      <label
                        class="text-sm font-medium text-gray-500 dark:text-gray-400"
                        >Total Taxable Amount</label
                      >
                      <p
                        class="text-sm text-gray-900 dark:text-white font-medium"
                      >
                        {{
                          calculatedTotTaxblAmt
                            ? `${calculatedTotTaxblAmt} RWF`
                            : "Not calculated"
                        }}
                      </p>
                    </div>
                    <div>
                      <label
                        class="text-sm font-medium text-gray-500 dark:text-gray-400"
                        >Total Tax Amount</label
                      >
                      <p
                        class="text-sm text-gray-900 dark:text-white font-medium"
                      >
                        {{
                          calculatedTotTaxAmt
                            ? `${calculatedTotTaxAmt} RWF`
                            : "Not calculated"
                        }}
                      </p>
                    </div>
                    <div>
                      <label
                        class="text-sm font-medium text-gray-500 dark:text-gray-400"
                        >Receipt Type Code</label
                      >
                      <p
                        class="text-sm text-gray-900 dark:text-white font-medium"
                      >
                        {{
                          transformedEbmPurchaseData.rcptTyCd || "Not specified"
                        }}
                      </p>
                    </div>
                    <div>
                      <label
                        class="text-sm font-medium text-gray-500 dark:text-gray-400"
                        >Payment Type Code</label
                      >
                      <p
                        class="text-sm text-gray-900 dark:text-white font-medium"
                      >
                        {{
                          transformedEbmPurchaseData.pmtTyCd || "Not specified"
                        }}
                      </p>
                    </div>
                    <div>
                      <label
                        class="text-sm font-medium text-gray-500 dark:text-gray-400"
                        >Purchase Status Code</label
                      >
                      <p
                        class="text-sm text-gray-900 dark:text-white font-medium"
                      >
                        {{
                          transformedEbmPurchaseData.pchsSttsCd ||
                          "Not specified"
                        }}
                      </p>
                    </div>
                    <div>
                      <label
                        class="text-sm font-medium text-gray-500 dark:text-gray-400"
                        >Purchase Date</label
                      >
                      <p
                        class="text-sm text-gray-900 dark:text-white font-medium"
                      >
                        {{ pchsDtDisplay || "Not specified" }}
                      </p>
                    </div>
                  </div>
                </div>

                <!-- Level 4: Supplier Information -->
                <div
                  class="bg-orange-50 dark:bg-orange-900/20 rounded-lg p-4 border border-orange-200 dark:border-orange-700"
                >
                  <h5
                    class="text-sm font-semibold text-orange-800 dark:text-orange-300 mb-3 flex items-center"
                  >
                    <vue-feather type="truck" size="16" class="mr-2" />
                    Supplier Information
                  </h5>
                  <div
                    class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4"
                  >
                    <div>
                      <label
                        class="text-sm font-medium text-gray-500 dark:text-gray-400"
                        >Supplier TIN</label
                      >
                      <p
                        class="text-sm text-gray-900 dark:text-white font-medium"
                      >
                        {{
                          transformedEbmPurchaseData.spplrTin || "Not specified"
                        }}
                      </p>
                    </div>
                    <div>
                      <label
                        class="text-sm font-medium text-gray-500 dark:text-gray-400"
                        >Supplier Branch ID</label
                      >
                      <p
                        class="text-sm text-gray-900 dark:text-white font-medium"
                      >
                        {{
                          transformedEbmPurchaseData.spplrBhfId ||
                          "Not specified"
                        }}
                      </p>
                    </div>
                    <div>
                      <label
                        class="text-sm font-medium text-gray-500 dark:text-gray-400"
                        >Supplier Name</label
                      >
                      <p
                        class="text-sm text-gray-900 dark:text-white font-medium"
                      >
                        {{
                          transformedEbmPurchaseData.spplrNm || "Not specified"
                        }}
                      </p>
                    </div>
                    <div>
                      <label
                        class="text-sm font-medium text-gray-500 dark:text-gray-400"
                        >Supplier Invoice Number</label
                      >
                      <p
                        class="text-sm text-gray-900 dark:text-white font-medium"
                      >
                        {{
                          transformedEbmPurchaseData.spplrInvcNo ||
                          "Not specified"
                        }}
                      </p>
                    </div>
                    <div>
                      <label
                        class="text-sm font-medium text-gray-500 dark:text-gray-400"
                        >Registrant ID</label
                      >
                      <p
                        class="text-sm text-gray-900 dark:text-white font-medium"
                      >
                        {{
                          transformedEbmPurchaseData.regrId || "Not specified"
                        }}
                      </p>
                    </div>
                    <div>
                      <label
                        class="text-sm font-medium text-gray-500 dark:text-gray-400"
                        >Registrant Name</label
                      >
                      <p
                        class="text-sm text-gray-900 dark:text-white font-medium"
                      >
                        {{
                          transformedEbmPurchaseData.regrNm || "Not specified"
                        }}
                      </p>
                    </div>
                    <div>
                      <label
                        class="text-sm font-medium text-gray-500 dark:text-gray-400"
                        >Modifier Name</label
                      >
                      <p
                        class="text-sm text-gray-900 dark:text-white font-medium"
                      >
                        {{
                          transformedEbmPurchaseData.modrNm || "Not specified"
                        }}
                      </p>
                    </div>
                    <div>
                      <label
                        class="text-sm font-medium text-gray-500 dark:text-gray-400"
                        >Modifier ID</label
                      >
                      <p
                        class="text-sm text-gray-900 dark:text-white font-medium"
                      >
                        {{
                          transformedEbmPurchaseData.modrId || "Not specified"
                        }}
                      </p>
                    </div>
                  </div>
                </div>

                <!-- Level 5: Tax Breakdown -->
                <div
                  class="bg-indigo-50 dark:bg-indigo-900/20 rounded-lg p-4 border border-indigo-200 dark:border-indigo-700"
                >
                  <h5
                    class="text-sm font-semibold text-indigo-800 dark:text-indigo-300 mb-3 flex items-center"
                  >
                    <vue-feather type="hash" size="16" class="mr-2" />
                    Tax Breakdown
                  </h5>
                  <div
                    class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4"
                  >
                    <div>
                      <label
                        class="text-sm font-medium text-gray-500 dark:text-gray-400"
                        >Taxable Amount A</label
                      >
                      <p
                        class="text-sm text-gray-900 dark:text-white font-medium"
                      >
                        {{
                          transformedEbmPurchaseData.taxblAmtA
                            ? formatDisplayNumber(
                                transformedEbmPurchaseData.taxblAmtA
                              )
                            : "Not specified"
                        }}
                      </p>
                    </div>
                    <div>
                      <label
                        class="text-sm font-medium text-gray-500 dark:text-gray-400"
                        >Taxable Amount B</label
                      >
                      <p
                        class="text-sm text-gray-900 dark:text-white font-medium"
                      >
                        {{
                          transformedEbmPurchaseData.taxblAmtB
                            ? formatDisplayNumber(
                                transformedEbmPurchaseData.taxblAmtB
                              )
                            : "Not specified"
                        }}
                      </p>
                    </div>
                    <div>
                      <label
                        class="text-sm font-medium text-gray-500 dark:text-gray-400"
                        >Taxable Amount C</label
                      >
                      <p
                        class="text-sm text-gray-900 dark:text-white font-medium"
                      >
                        {{
                          transformedEbmPurchaseData.taxblAmtC
                            ? formatDisplayNumber(
                                transformedEbmPurchaseData.taxblAmtC
                              )
                            : "Not specified"
                        }}
                      </p>
                    </div>
                    <div>
                      <label
                        class="text-sm font-medium text-gray-500 dark:text-gray-400"
                        >Taxable Amount D</label
                      >
                      <p
                        class="text-sm text-gray-900 dark:text-white font-medium"
                      >
                        {{
                          transformedEbmPurchaseData.taxblAmtD
                            ? formatDisplayNumber(
                                transformedEbmPurchaseData.taxblAmtD
                              )
                            : "Not specified"
                        }}
                      </p>
                    </div>
                    <div>
                      <label
                        class="text-sm font-medium text-gray-500 dark:text-gray-400"
                        >Tax Rate A</label
                      >
                      <p
                        class="text-sm text-gray-900 dark:text-white font-medium"
                      >
                        {{
                          transformedEbmPurchaseData.taxRtA || "Not specified"
                        }}
                      </p>
                    </div>
                    <div>
                      <label
                        class="text-sm font-medium text-gray-500 dark:text-gray-400"
                        >Tax Rate B</label
                      >
                      <p
                        class="text-sm text-gray-900 dark:text-white font-medium"
                      >
                        {{
                          transformedEbmPurchaseData.taxRtB || "Not specified"
                        }}
                      </p>
                    </div>
                    <div>
                      <label
                        class="text-sm font-medium text-gray-500 dark:text-gray-400"
                        >Tax Rate C</label
                      >
                      <p
                        class="text-sm text-gray-900 dark:text-white font-medium"
                      >
                        {{
                          transformedEbmPurchaseData.taxRtC || "Not specified"
                        }}
                      </p>
                    </div>
                    <div>
                      <label
                        class="text-sm font-medium text-gray-500 dark:text-gray-400"
                        >Tax Rate D</label
                      >
                      <p
                        class="text-sm text-gray-900 dark:text-white font-medium"
                      >
                        {{
                          transformedEbmPurchaseData.taxRtD || "Not specified"
                        }}
                      </p>
                    </div>
                    <div>
                      <label
                        class="text-sm font-medium text-gray-500 dark:text-gray-400"
                        >Tax Amount A</label
                      >
                      <p
                        class="text-sm text-gray-900 dark:text-white font-medium"
                      >
                        {{
                          transformedEbmPurchaseData.taxAmtA
                            ? formatDisplayNumber(
                                transformedEbmPurchaseData.taxAmtA
                              )
                            : "Not specified"
                        }}
                      </p>
                    </div>
                    <div>
                      <label
                        class="text-sm font-medium text-gray-500 dark:text-gray-400"
                        >Tax Amount B</label
                      >
                      <p
                        class="text-sm text-gray-900 dark:text-white font-medium"
                      >
                        {{
                          transformedEbmPurchaseData.taxAmtB
                            ? formatDisplayNumber(
                                transformedEbmPurchaseData.taxAmtB
                              )
                            : "Not specified"
                        }}
                      </p>
                    </div>
                    <div>
                      <label
                        class="text-sm font-medium text-gray-500 dark:text-gray-400"
                        >Tax Amount C</label
                      >
                      <p
                        class="text-sm text-gray-900 dark:text-white font-medium"
                      >
                        {{
                          transformedEbmPurchaseData.taxAmtC
                            ? formatDisplayNumber(
                                transformedEbmPurchaseData.taxAmtC
                              )
                            : "Not specified"
                        }}
                      </p>
                    </div>
                    <div>
                      <label
                        class="text-sm font-medium text-gray-500 dark:text-gray-400"
                        >Tax Amount D</label
                      >
                      <p
                        class="text-sm text-gray-900 dark:text-white font-medium"
                      >
                        {{
                          transformedEbmPurchaseData.taxAmtD
                            ? formatDisplayNumber(
                                transformedEbmPurchaseData.taxAmtD
                              )
                            : "Not specified"
                        }}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Section 3: Combined Calculations & Alerts -->
            <div
              class="bg-gradient-to-r from-yellow-50 to-orange-50 dark:from-yellow-900/20 dark:to-orange-900/20 rounded-xl border border-yellow-200 dark:border-yellow-700 p-6"
            >
              <h4
                class="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center"
              >
                <vue-feather
                  type="hash"
                  size="20"
                  class="mr-3 text-yellow-600"
                />
                Combined Calculations & Alerts
              </h4>

              <!-- Totals Section -->
              <div
                class="bg-green-50 dark:bg-green-900/20 rounded-lg p-4 border border-green-200 dark:border-green-700 mb-4"
              >
                <h5
                  class="text-sm font-semibold text-green-800 dark:text-green-300 mb-3 flex items-center"
                >
                  <vue-feather type="check-circle" size="16" class="mr-2" />
                  ✅ Totals
                </h5>
                <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label
                      class="text-sm font-medium text-gray-500 dark:text-gray-400"
                      >Total Amount</label
                    >
                    <p
                      class="text-lg font-semibold text-green-600 dark:text-green-400"
                    >
                      {{
                        calculatedTotAmt
                          ? `${calculatedTotAmt} RWF`
                          : "Not calculated"
                      }}
                    </p>
                  </div>
                  <div>
                    <label
                      class="text-sm font-medium text-gray-500 dark:text-gray-400"
                      >Taxable Amount</label
                    >
                    <p
                      class="text-lg font-semibold text-green-600 dark:text-green-400"
                    >
                      {{
                        calculatedTotTaxblAmt
                          ? `${calculatedTotTaxblAmt} RWF`
                          : "Not calculated"
                      }}
                    </p>
                  </div>
                  <div>
                    <label
                      class="text-sm font-medium text-gray-500 dark:text-gray-400"
                      >Tax Amount</label
                    >
                    <p
                      class="text-lg font-semibold text-green-600 dark:text-green-400"
                    >
                      {{
                        calculatedTotTaxAmt
                          ? `${calculatedTotTaxAmt} RWF`
                          : "Not calculated"
                      }}
                    </p>
                  </div>
                </div>
              </div>

              <!-- Validation Messages -->
              <div
                v-if="validationErrors.length > 0"
                class="bg-red-50 dark:bg-red-900/20 rounded-lg p-4 border border-red-200 dark:border-red-700 mb-4"
              >
                <h5
                  class="text-sm font-semibold text-red-800 dark:text-red-300 mb-3 flex items-center"
                >
                  <vue-feather type="alert-triangle" size="16" class="mr-2" />
                  ⚠️ Validation Messages
                </h5>
                <ul class="space-y-2">
                  <li
                    v-for="error in validationErrors"
                    :key="error.field"
                    class="text-sm text-red-700 dark:text-red-300"
                  >
                    • {{ error.message }}
                  </li>
                </ul>
              </div>

              <!-- System Notes -->
              <div
                class="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4 border border-blue-200 dark:border-blue-700"
              >
                <h5
                  class="text-sm font-semibold text-blue-800 dark:text-blue-300 mb-3 flex items-center"
                >
                  <vue-feather type="info" size="16" class="mr-2" />
                  🔒 System Notes
                </h5>
                <div class="text-sm text-blue-700 dark:text-blue-300 space-y-2">
                  <p v-if="isPurchaseDataDisabled">
                    • Purchase data has already been saved to EBM and cannot be
                    edited
                  </p>
                  <p v-if="hasQuantityMismatch">
                    • Quantity mismatch detected between product and purchase
                    data
                  </p>
                  <p v-if="!hasPurchaseEbmMetadata">
                    • Product data will be saved to EBM
                  </p>
                  <p v-else>
                    • Only product data can be updated (purchase data already
                    exists in EBM)
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

    </div>

    <!-- Modal Footer -->
    <template #footer>
          <hs-button variant="secondary" @click="handleClose">Cancel</hs-button>
          <!-- Product Tab: Continue/Save Product button -->
          <hs-button
            v-if="activeTab === 'product'"
            icon="arrow-right"
            :disabled="
              loading ||
              validationErrors.length > 0 ||
              (isEditingDisabled && !hasPurchaseEbmMetadata)
            "
            :loading="loading"
            @click="handleSave"
          >
            {{ loading ? "Continuing..." : saveButtonText }}
          </hs-button>

          <!-- Purchase Tab: Preview Changes button -->
          <hs-button
            v-if="activeTab === 'purchase'"
            variant="success"
            icon="eye"
            :disabled="
              loading ||
              validationErrors.length > 0 ||
              isEditingDisabled ||
              !hasProductPurchaseId
            "
            :loading="loading"
            @click="handleSave"
          >
            {{ loading ? "Previewing..." : previewButtonText }}
          </hs-button>

          <!-- Preview Tab: Final Submit button -->
          <hs-button
            v-if="activeTab === 'preview'"
            variant="success"
            icon="send"
            :disabled="
              loading ||
              validationErrors.length > 0 ||
              isEditingDisabled ||
              !hasProductPurchaseId
            "
            :loading="loading"
            @click="handleSave"
          >
            {{ loading ? "Submitting..." : "Submit to EBM" }}
          </hs-button>
    </template>
  </hs-modal>
</template>

<script>
import {
  ItemSaveReqValidator,
  TrnsPurchaseSaveReqValidator,
  EbmDataUtils,
} from "@/utils/ebmValidation";
import http from "@/helper/http";
import EbmSelectDropdown from "@/components/common/EbmSelectDropdown.vue";
import { HsButton, HsDateInput, HsModal } from "@/components/ui";

export default {
  name: "EbmDataEditorModal",
  props: {
    isOpen: {
      type: Boolean,
      required: true,
    },
    inventoryItem: {
      type: Object,
      default: null,
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  components: {
    EbmSelectDropdown,
    HsButton,
    HsDateInput,
    HsModal,
  },
  data() {
    return {
      activeTab: "product",
      isLoadingData: false,
      loadingMessage: "Loading EBM data...",
      fetchedProductData: null,
      fetchedPurchaseData: null,
      showLevel2Fields: false,
      showLevel3Fields: false,
      showPurchaseLevel2Fields: false,
      showPurchaseLevel3Fields: false,
      showPurchaseLevel4Fields: false,
      showPurchaseLevel5Fields: false,
      showPreviewProductDetails: false,
      showPreviewPurchaseDetails: false,
      showQuantityMismatchDetails: false,
      formData: {
        ebmItem: {
          // Required CHAR fields
          tin: "",
          bhfId: "",
          itemCd: "",
          itemClsCd: "",
          itemTyCd: "2",
          itemNm: "",
          orgnNatCd: "RW",
          pkgUnitCd: "NT",
          qtyUnitCd: "U",
          taxTyCd: "B",
          isrcAplcbYn: "N",
          useYn: "Y",
          // Required NUMBER field
          dftPrc: 0,
          // Optional CHAR fields
          itemStdNm: null,
          btchNo: null,
          bcd: null,
          addInfo: null,
          // Optional NUMBER fields
          grpPrcL1: 0,
          grpPrcL2: 0,
          grpPrcL3: 0,
          grpPrcL4: 0,
          grpPrcL5: 0,
          sftyQty: 0,
          // Additional fields
          rraModYn: "N",
          regBhfId: null,
          regrNm: "",
          regrId: "",
          modrNm: "",
          modrId: "",
        },
        ebmPurchase: {
          // Required string fields
          tin: "",
          bhfId: "",
          regTyCd: "A",
          pchsTyCd: "N",
          rcptTyCd: "P",
          pmtTyCd: "01",
          pchsSttsCd: "02",
          pchsDt: "",
          regrNm: "Admin",
          regrId: "Admin",
          modrNm: "Admin",
          modrId: "Admin",
          // Required integer fields
          invcNo: 0,
          orgInvcNo: 0,
          totItemCnt: 1,
          // Required number fields
          taxblAmtA: 0,
          taxblAmtB: 0,
          taxblAmtC: 0,
          taxblAmtD: 0,
          taxRtA: 0,
          taxRtB: 18,
          taxRtC: 0,
          taxRtD: 0,
          taxAmtA: 0,
          taxAmtB: 0,
          taxAmtC: 0,
          taxAmtD: 0,
          totTaxblAmt: 0,
          totTaxAmt: 0,
          totAmt: 0,
          // Supplier fields
          spplrTin: "999999999",
          spplrBhfId: "00",
          spplrNm: "Supplier",
          spplrInvcNo: 1,
          // Optional fields
          spplrSdcId: null,
          spplrItemCd: null,
          cfmDt: null,
          wrhsDt: null,
          cnclReqDt: null,
          cnclDt: null,
          rfdDt: null,
          remark: null,
          itemList: [],
          // For the one item
          prc: 0,
          qty: 0,
        },
      },
      validationErrors: [],
      originalData: null,
      selectedQuantityOptions: [], // Array for checkbox selections
      selectedQuantityOption: "original", // Keep for backward compatibility
      editableQuantity: 0, // Direct editable quantity field
      quantityTimeout: null, // For debouncing quantity changes
      priceTimeout: null, // For debouncing price changes
      totalAmountTimeout: null, // For debouncing total amount changes
      userEditedFields: new Set(), // Track which fields user has manually edited
      showPurchaseDetails: false, // Track expanded state for purchase details
      showAdvancedFields: false, // Track expanded state for advanced fields
    };
  },
  computed: {
    tabs() {
      return [
        { id: "product", name: "EBM Product Data" },
        { id: "purchase", name: "EBM Purchase Data" },
        { id: "preview", name: "Preview & Submit" },
      ];
    },
    // Check if a tab should be disabled
    isTabDisabled() {
      return (tabId) => {
        // Disable purchase and preview tabs when hasProductPurchaseId is not defined
        if (
          (tabId === "purchase" || tabId === "preview") &&
          !this.hasProductPurchaseId
        ) {
          return true;
        }
        return false;
      };
    },
    transformedEbmItemData() {
      return this.formData.ebmItem;
    },

    // Save button text and behavior
    saveButtonText() {
      if (this.hasPurchaseEbmMetadata) {
        return "Save Product";
      }
      if (!this.hasProductPurchaseId) {
        return "Save Product";
      }
      return "Continue";
    },

    saveButtonAction() {
      if (this.hasPurchaseEbmMetadata) {
        return "save";
      }
      if (!this.hasProductPurchaseId) {
        return "save";
      }
      return "continue";
    },

    // Preview button text and behavior for Purchase tab
    previewButtonText() {
      return "Preview Changes";
    },

    previewButtonAction() {
      return "preview";
    },

    // Computed property to handle date display conversion
    pchsDtDisplay: {
      get() {
        // Convert EBM format (YYYYMMDD) to HTML date format (YYYY-MM-DD)
        return this.parseDate(this.formData.ebmPurchase.pchsDt);
      },
      set(value) {
        // Convert HTML date format (YYYY-MM-DD) to EBM format (YYYYMMDD)
        this.formData.ebmPurchase.pchsDt = this.parseEbmDate(value);
      },
    },
    transformedEbmPurchaseData() {
      return this.formData.ebmPurchase;
    },
    hasExistingEbmMetadata() {
      return this.inventoryItem?.product?.has_rra_ebm_metadata || false;
    },
    isDataInitialized() {
      // Data is considered initialized if:
      // 1. We have fetched data from API, OR
      // 2. We have initialized with default data (originalData exists), AND
      // 3. We're not currently loading
      return (
        !this.isLoadingData &&
        (this.fetchedProductData !== null || this.originalData !== null) &&
        this.inventoryItem?.product?.id
      );
    },
    hasPurchaseEbmMetadata() {
      // Check if the fetched purchase data already has EBM metadata
      return !!(
        this.fetchedPurchaseData && this.fetchedPurchaseData.rra_ebm_metadata
      );
    },
    isEditingDisabled() {
      // Disable editing if purchase data already has EBM metadata
      return this.hasPurchaseEbmMetadata;
    },
    // Check if product_purchase_id is available
    hasProductPurchaseId() {
      return !!(this.fetchedPurchaseData && this.fetchedPurchaseData.id);
    },
    // Get the selected quantity based on user's radio selection or direct editing
    selectedPurchaseQuantity() {
      // If user has directly edited the quantity field, use that value
      if (this.editableQuantity > 0) {
        return this.editableQuantity;
      }
      // If we have fetched purchase data, use its quantity as default
      if (this.fetchedPurchaseData?.quantity) {
        return this.parseNumber(this.fetchedPurchaseData.quantity);
      }
      // Otherwise, use the radio button selection
      return this.selectedQuantityOption === "original"
        ? this.originalPurchaseQuantity
        : this.correctedPurchaseQuantity;
    },

    // Reactive calculated totals
    calculatedTotTaxblAmt() {
      const qty = this.parseNumber(this.formData.ebmPurchase.qty);
      const prc = this.parseNumber(this.formData.ebmPurchase.prc);
      console.log("🧮 calculatedTotTaxblAmt called:", { qty, prc });
      if (qty <= 0 || prc <= 0) return "0.00";
      const result = (prc * qty).toFixed(2);
      console.log("🧮 calculatedTotTaxblAmt result:", result);
      return this.formatDisplayNumber(result);
    },

    calculatedTotTaxAmt() {
      const qty = this.parseNumber(this.formData.ebmPurchase.qty);
      const prc = this.parseNumber(this.formData.ebmPurchase.prc);
      const taxRate =
        this.formData.ebmItem.taxTyCd === "B"
          ? this.parseNumber(this.formData.ebmPurchase.taxRtB)
          : 0;
      console.log("🧮 calculatedTotTaxAmt called:", {
        qty,
        prc,
        taxRate,
        taxTyCd: this.formData.ebmItem.taxTyCd,
      });
      if (qty <= 0 || prc <= 0 || taxRate <= 0) return "0.00";
      const result = (prc * qty * (taxRate / 100)).toFixed(2);
      console.log("🧮 calculatedTotTaxAmt result:", result);
      return this.formatDisplayNumber(result);
    },

    calculatedTotAmt() {
      const qty = this.parseNumber(this.formData.ebmPurchase.qty);
      const prc = this.parseNumber(this.formData.ebmPurchase.prc);
      const taxRate =
        this.formData.ebmItem.taxTyCd === "B"
          ? this.parseNumber(this.formData.ebmPurchase.taxRtB)
          : 0;
      if (qty <= 0 || prc <= 0) return "0.00";
      const taxableAmount = prc * qty;
      const taxAmount = taxableAmount * (taxRate / 100);
      const result = (taxableAmount + taxAmount).toFixed(2);
      return this.formatDisplayNumber(result);
    },
    // Check if purchase data fields should be disabled
    isPurchaseDataDisabled() {
      return this.hasPurchaseEbmMetadata;
    },
    // Check if always-locked fields should be disabled
    isAlwaysLockedFieldDisabled() {
      // These fields are editable only when no existing EBM metadata
      return this.hasExistingEbmMetadata;
    },
    // Check for quantity mismatch between inventory and purchase
    hasQuantityMismatch() {
      if (!this.inventoryItem || !this.fetchedPurchaseData) {
        return false;
      }
      const inventoryQuantity = this.parseNumber(this.inventoryItem.quantity);
      const purchaseQuantity = this.parseNumber(
        this.fetchedPurchaseData?.quantity
      );
      return inventoryQuantity !== purchaseQuantity;
    },
    // Get inventory quantity for comparison
    inventoryQuantity() {
      return this.parseNumber(this.inventoryItem?.quantity || 0);
    },
    // Get original purchase quantity (from server data)
    originalPurchaseQuantity() {
      return this.parseNumber(this.fetchedPurchaseData?.quantity || 0);
    },
    // Get corrected purchase quantity (inventory quantity)
    correctedPurchaseQuantity() {
      return this.inventoryQuantity;
    },
  },
  watch: {
    // Watch for quantity mismatches and log them
    hasQuantityMismatch: {
      handler(hasMismatch) {
        if (hasMismatch && this.inventoryQuantity > 0) {
          // Set default selection to corrected value when mismatch is detected
          this.selectedQuantityOption = "corrected";
          this.selectedQuantityOptions = ["corrected"]; // Also set checkbox selection
        }
      },
      immediate: false,
    },

    // Watch for radio selection changes and apply the selected quantity
    selectedQuantityOption: {
      handler(newOption) {
        if (this.hasQuantityMismatch && !this.isPurchaseDataDisabled) {
          console.log("🔄 Radio selection changed to:", newOption);
          if (newOption === "original") {
            this.useOriginalQuantity();
          } else if (newOption === "corrected") {
            this.useCorrectedQuantity();
          }
        }
      },
    },

    isOpen: {
      handler(isOpen) {
        console.log("EbmDataEditorModal: isOpen watcher triggered", {
          isOpen,
          hasInventoryItem: !!this.inventoryItem,
          productId: this.inventoryItem?.product?.id,
        });
        if (isOpen) {
          console.log("EbmDataEditorModal: Modal opened, initializing data");
          // Small delay to ensure the modal is fully rendered
          this.$nextTick(() => {
            this.initializeModalData();
          });
        }
      },
      immediate: true,
    },
    inventoryItem: {
      handler(newItem, oldItem) {
        console.log("EbmDataEditorModal: inventoryItem watcher triggered", {
          newItem,
          oldItem,
          hasProduct: !!newItem?.product,
          productId: newItem?.product?.id,
          isOpen: this.isOpen,
        });
        if (newItem && newItem.product && newItem.product.id && this.isOpen) {
          console.log(
            "EbmDataEditorModal: Valid inventory item received, initializing modal data"
          );
          this.$nextTick(() => {
            this.initializeModalData();
          });
        } else if (newItem && !newItem.product?.id && this.isOpen) {
          console.warn(
            "EbmDataEditorModal: Invalid inventory item data",
            newItem
          );
          this.initializeFormDataWithEbmSettings();
        }
      },
      immediate: true,
    },
    formData: {
      handler() {
        // Skip automatic validation - only validate on explicit save
        console.log(
          "EbmDataEditorModal: formData changed but skipping automatic validation"
        );
      },
      deep: true,
    },
    isDataInitialized: {
      handler(isInitialized) {
        console.log(
          "EbmDataEditorModal: isDataInitialized changed to:",
          isInitialized
        );
        if (isInitialized && this.isOpen) {
          console.log(
            "EbmDataEditorModal: Data became initialized but skipping automatic validation"
          );
        }
      },
      immediate: true,
    },
    // Watch for changes in taxable amounts to recalculate totals
    "formData.ebmPurchase.taxblAmtA": {
      handler() {
        this.recalculateTotals();
      },
    },
    "formData.ebmPurchase.taxblAmtB": {
      handler() {
        this.recalculateTotals();
      },
    },
    "formData.ebmPurchase.taxblAmtC": {
      handler() {
        this.recalculateTotals();
      },
    },
    "formData.ebmPurchase.taxblAmtD": {
      handler() {
        this.recalculateTotals();
      },
    },
    // Watch for changes in tax amounts to recalculate totals
    "formData.ebmPurchase.taxAmtA": {
      handler() {
        this.recalculateTotals();
      },
    },
    "formData.ebmPurchase.taxAmtB": {
      handler() {
        this.recalculateTotals();
      },
    },
    "formData.ebmPurchase.taxAmtC": {
      handler() {
        this.recalculateTotals();
      },
    },
    "formData.ebmPurchase.taxAmtD": {
      handler() {
        this.recalculateTotals();
      },
    },
    // Watch for changes in tax type code to recalculate tax amounts
    "formData.ebmItem.taxTyCd": {
      handler(newTaxType, oldTaxType) {
        console.log("🔄 Tax type changed:", {
          oldTaxType,
          newTaxType,
        });
        // Clear user edit tracking when tax type changes
        this.clearUserEditTracking();
        this.recalculateTaxAmountsForTaxType(newTaxType);
      },
    },
    // Watch for changes in sale unit price to recalculate tax amounts and update group prices
    "formData.ebmItem.dftPrc": {
      handler(newPrice, oldPrice) {
        console.log("💰 Sale unit price changed:", {
          oldPrice,
          newPrice,
        });

        // Clear validation errors for this field when value becomes valid
        if (newPrice !== oldPrice) {
          const parsedPrice = this.parseNumber(newPrice);
          if (parsedPrice > 0) {
            // Remove validation errors for this specific field
            this.clearFieldValidationError("ebmItem.dftPrc");
            this.clearFieldValidationError("dftPrc"); // Also clear if field path is just 'dftPrc'
          } else if (parsedPrice <= 0) {
            // Just clear the validation error for this field, don't run full validation
            this.clearFieldValidationError("ebmItem.dftPrc");
            this.clearFieldValidationError("dftPrc");
          }
        }

        // Only recalculate if values are actually different and valid
        if (newPrice !== oldPrice && this.parseNumber(newPrice) >= 0) {
          // Update all group prices to match sale unit price
          const defaultPrice = this.parseNumber(newPrice);
          this.formData.ebmItem.grpPrcL1 = defaultPrice;
          this.formData.ebmItem.grpPrcL2 = defaultPrice;
          this.formData.ebmItem.grpPrcL3 = defaultPrice;
          this.formData.ebmItem.grpPrcL4 = defaultPrice;
          this.formData.ebmItem.grpPrcL5 = defaultPrice;

          // Clear any existing timeout
          if (this.priceTimeout) {
            clearTimeout(this.priceTimeout);
          }
          // Debounce the recalculation to prevent focus loss
          this.priceTimeout = setTimeout(() => {
            this.recalculateTaxAmountsForTaxType(this.formData.ebmItem.taxTyCd);
          }, 500); // 500ms delay
        }
      },
    },
    // Watch for changes in purchase unit price to clear validation errors
    "formData.ebmPurchase.prc": {
      handler(newPrice, oldPrice) {
        console.log("💰 Purchase unit price changed:", {
          oldPrice,
          newPrice,
        });

        // Clear validation errors for this field when value becomes valid
        if (newPrice !== oldPrice) {
          const parsedPrice = this.parseNumber(newPrice);
          if (parsedPrice > 0) {
            // Remove validation errors for this specific field
            this.clearFieldValidationError("ebmPurchase.prc");

            // Clear any existing timeout
            if (this.priceTimeout) {
              clearTimeout(this.priceTimeout);
            }

            // Debounce the tax recalculation
            this.priceTimeout = setTimeout(() => {
              console.log("🔄 Triggering tax recalculation due to prc change");
              this.recalculateTaxAmountsForTaxType(
                this.formData.ebmItem.taxTyCd
              );
            }, 300); // 300ms delay
          }
        }
      },
    },
    // Watch for changes in purchase quantity to trigger tax recalculation
    "formData.ebmPurchase.qty": {
      handler(newQty, oldQty) {
        console.log("📦 Purchase quantity changed:", {
          oldQty,
          newQty,
        });

        // Only recalculate if values are actually different and valid
        if (newQty !== oldQty && this.parseNumber(newQty) >= 0) {
          // Clear any existing timeout
          if (this.quantityTimeout) {
            clearTimeout(this.quantityTimeout);
          }

          // Debounce the recalculation to prevent focus loss
          this.quantityTimeout = setTimeout(() => {
            console.log("🔄 Triggering tax recalculation due to qty change");
            this.recalculateTaxAmountsForTaxType(this.formData.ebmItem.taxTyCd);
          }, 300); // 300ms delay
        }
      },
    },
    // Watch for changes in total item count to recalculate tax amounts
    "formData.ebmPurchase.totItemCnt": {
      handler(newItemCount, oldItemCount) {
        console.log("📦 Total item count changed:", {
          oldItemCount,
          newItemCount,
        });
        // Only recalculate if values are actually different and valid
        if (
          newItemCount !== oldItemCount &&
          this.parseNumber(newItemCount) >= 0
        ) {
          // Clear user edit tracking when quantity changes
          this.clearUserEditTracking();
          // Clear any existing timeout
          if (this.quantityTimeout) {
            clearTimeout(this.quantityTimeout);
          }
          // Debounce the recalculation to prevent focus loss
          this.quantityTimeout = setTimeout(() => {
            this.recalculateTaxAmountsForTaxType(this.formData.ebmItem.taxTyCd);
          }, 500); // 500ms delay
        }
      },
    },
    // Watch for changes in total amount to calculate other fields
    "formData.ebmPurchase.totAmt": {
      handler(newTotalAmount, oldTotalAmount) {
        console.log("💰 Total amount changed:", {
          oldTotalAmount,
          newTotalAmount,
        });
        // Only recalculate if values are actually different and valid
        if (
          newTotalAmount !== oldTotalAmount &&
          this.parseNumber(newTotalAmount) >= 0
        ) {
          // Clear any existing timeout
          if (this.totalAmountTimeout) {
            clearTimeout(this.totalAmountTimeout);
          }
          // Debounce the recalculation to prevent focus loss
          this.totalAmountTimeout = setTimeout(() => {
            this.calculateFieldsFromTotalAmount();
          }, 500); // 500ms delay
        }
      },
    },
    // Watch for changes in editableQuantity to sync with formData.ebmPurchase.qty
    editableQuantity: {
      handler(newQuantity, oldQuantity) {
        // Only update if the value actually changed and is valid
        if (newQuantity !== oldQuantity && this.parseNumber(newQuantity) >= 0) {
          console.log(
            "🔄 editableQuantity changed, syncing with formData.ebmPurchase.qty:",
            {
              oldQuantity,
              newQuantity,
              formDataQty: this.formData.ebmPurchase.qty,
            }
          );

          // Update formData.ebmPurchase.qty to match editableQuantity
          // Note: totItemCnt should remain 1 as it represents the count of items in itemList, not the quantity of the item
          const parsedQty = this.parseNumber(newQuantity);
          this.formData.ebmPurchase.qty = parsedQty;
          console.log(
            "🔄 Updated formData.ebmPurchase.qty to:",
            parsedQty,
            "from editableQuantity:",
            newQuantity
          );

          // Clear any existing timeout
          if (this.quantityTimeout) {
            clearTimeout(this.quantityTimeout);
          }

          // Debounce the recalculation to prevent focus loss
          this.quantityTimeout = setTimeout(() => {
            this.recalculateTaxAmountsForTaxType(this.formData.ebmItem.taxTyCd);
          }, 500); // 500ms delay
        }
      },
      immediate: false,
    },
  },
  beforeUnmount() {
    // Clean up timeouts to prevent memory leaks
    if (this.quantityTimeout) {
      clearTimeout(this.quantityTimeout);
    }
    if (this.priceTimeout) {
      clearTimeout(this.priceTimeout);
    }
    if (this.totalAmountTimeout) {
      clearTimeout(this.totalAmountTimeout);
    }
  },
  mounted() {
    console.log("EbmDataEditorModal: Component mounted", {
      isOpen: this.isOpen,
      hasInventoryItem: !!this.inventoryItem,
      productId: this.inventoryItem?.product?.id,
    });

    // If modal is already open when component mounts, initialize data
    if (this.isOpen && this.inventoryItem?.product?.id) {
      console.log(
        "EbmDataEditorModal: Modal already open on mount, initializing data"
      );
      this.$nextTick(() => {
        this.initializeModalData();
      });
    }
  },
  methods: {
    // Format number with thousands separators for display only
    formatDisplayNumber(value) {
      if (!value || value === "0.00" || value === "0") return "0.00";
      const num = parseFloat(value);
      if (isNaN(num)) return "0.00";
      return num.toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },

    // Handle checkbox changes for quantity options
    handleQuantityOptionChange() {
      // Ensure only one option is selected at a time
      if (this.selectedQuantityOptions.length > 1) {
        // If more than one is selected, keep only the last one
        const lastSelected =
          this.selectedQuantityOptions[this.selectedQuantityOptions.length - 1];
        this.selectedQuantityOptions = [lastSelected];
      }

      // Update the backward compatibility property
      if (this.selectedQuantityOptions.length === 1) {
        this.selectedQuantityOption = this.selectedQuantityOptions[0];
      } else {
        this.selectedQuantityOption = null; // No selection
      }

      console.log("🔄 Checkbox selection changed:", {
        selectedOptions: this.selectedQuantityOptions,
        selectedOption: this.selectedQuantityOption,
      });
    },

    initializeModalData() {
      console.log("EbmDataEditorModal: initializeModalData called", {
        isOpen: this.isOpen,
        hasInventoryItem: !!this.inventoryItem,
        inventoryItemKeys: this.inventoryItem
          ? Object.keys(this.inventoryItem)
          : [],
        productId: this.inventoryItem?.product?.id,
        hasProduct: !!this.inventoryItem?.product,
        hasPrice: !!this.inventoryItem?.price,
        priceValue: this.inventoryItem?.price?.unit_price,
        hasExistingMetadata: this.hasExistingEbmMetadata,
        fetchedProductData: this.fetchedProductData,
        originalData: this.originalData,
      });

      if (!this.isOpen) {
        console.log(
          "EbmDataEditorModal: Modal not open, skipping initialization"
        );
        return;
      }

      if (!this.inventoryItem?.product?.id) {
        console.warn(
          "EbmDataEditorModal: No valid inventory item, initializing with defaults"
        );
        console.warn(
          "EbmDataEditorModal: Full inventoryItem:",
          this.inventoryItem
        );
        this.initializeFormDataWithEbmSettings();
        return;
      }

      // Check if we already have data loaded to avoid duplicate calls
      if (this.fetchedProductData !== null || this.originalData !== null) {
        console.log("EbmDataEditorModal: Data already loaded, skipping fetch");
        return;
      }

      console.log(
        "EbmDataEditorModal: Fetching EBM metadata for product:",
        this.inventoryItem.product.id
      );
      this.fetchEbmMetadata();
    },

    async fetchEbmMetadata() {
      console.log("EbmDataEditorModal: fetchEbmMetadata called", {
        inventoryItem: this.inventoryItem,
        hasProduct: !!this.inventoryItem?.product,
        productId: this.inventoryItem?.product?.id,
      });

      if (!this.inventoryItem?.product?.id) {
        console.error(
          "EbmDataEditorModal: Invalid product data",
          this.inventoryItem
        );
        this.$emit("error", "Invalid product data");
        return;
      }

      this.isLoadingData = true;
      this.loadingMessage = "Loading EBM data...";

      try {
        const productId = this.inventoryItem.product.id;
        console.log(
          "EbmDataEditorModal: Fetching metadata for product ID:",
          productId
        );

        // Get EBM settings from store (already available)
        const ebmSettings = this.$store.getters.rraEbmSettings;
        console.log("EbmDataEditorModal: EBM settings:", ebmSettings);

        if (!ebmSettings?.ebm_activated) {
          console.warn("EbmDataEditorModal: EBM not activated");
          this.$emit(
            "error",
            "EBM is not activated for this store. Please activate EBM first."
          );
          this.isLoadingData = false;

          return;
        }

        // Fetch product EBM metadata
        this.loadingMessage = "Fetching product EBM metadata...";
        console.log(
          "EbmDataEditorModal: Making product API call to:",
          `/v1/store/product/${productId}?include=rra_ebm_metadata`
        );
        const productResponse = await http.get(
          `/v1/store/product/${productId}?include=rra_ebm_metadata`
        );
        console.log(
          "EbmDataEditorModal: Product API response:",
          productResponse.data
        );
        this.fetchedProductData = productResponse.data;

        // Fetch purchase EBM metadata
        this.loadingMessage = "Fetching purchase EBM metadata...";
        console.log(
          "EbmDataEditorModal: Making purchase API call to:",
          `/v2/store/product/purchases/product/${productId}?include=rra_ebm_metadata`
        );
        const purchaseResponse = await http.get(
          // - for the most recent purchase
          `/v2/store/product/purchases/product/${productId}?include=rra_ebm_metadata&per_page=1&sort=-created_at&deleted[eq]=0`
        );
        console.log(
          "EbmDataEditorModal: Purchase API response:",
          purchaseResponse.data
        );

        // Debug: Check if API call was successful
        if (!purchaseResponse.data) {
          console.error("❌ Purchase API returned no data");

          this.initializeFormDataWithMetadata(ebmSettings);
          this.isLoadingData = false;

          return;
        }

        if (
          !purchaseResponse.data.items ||
          purchaseResponse.data.items.length === 0
        ) {
          console.warn("⚠️ Purchase API returned no items");

          this.initializeFormDataWithMetadata(ebmSettings);
          this.isLoadingData = false;
          return;
        }
        // V2 API returns paginated data with EBM metadata, get the most recent (first) item
        this.fetchedPurchaseData = purchaseResponse.data?.items?.[0] || null;
        this.formData.ebmPurchase.qty = this.fetchedPurchaseData?.quantity || 0;
        this.formData.ebmPurchase.prc =
          this.fetchedPurchaseData?.unit_price || 0;

        // Debug: Check what's in the purchase data
        console.log("🔍 DEBUG: Checking purchase data for EBM metadata");

        // Debug: Check hasPurchaseEbmMetadata computed property
        console.log("🔍 DEBUG: hasPurchaseEbmMetadata computed property:", {
          hasPurchaseEbmMetadata: this.hasPurchaseEbmMetadata,
          fetchedPurchaseData: this.fetchedPurchaseData,
          rraEbmMetadata: this.fetchedPurchaseData?.rra_ebm_metadata,
        });

        // Check if purchase data already has EBM metadata
        if (this.hasPurchaseEbmMetadata) {
          console.log(
            "✅ Purchase data has EBM metadata - this is GOOD for prefilling!"
          );
          console.log(
            "🔧 Proceeding with form initialization using existing metadata"
          );
          // Don't return early - we want to prefill with this data!
        } else {
          console.log("❌ No EBM metadata found in purchase data");
        }

        this.loadingMessage = "Preparing form data...";

        // Initialize form data with fetched metadata and EBM settings
        this.initializeFormDataWithMetadata(ebmSettings);

        // Initialize editable quantity with the fetched purchase data quantity
        this.editableQuantity =
          this.fetchedPurchaseData?.quantity || this.formData.ebmPurchase.qty;

        // Small delay for smooth UX
        await new Promise((resolve) => setTimeout(resolve, 500));

        this.isLoadingData = false;

        // Data loaded successfully - validation will run on save
        console.log(
          "EbmDataEditorModal: Data loaded successfully, validation will run on save"
        );
      } catch (error) {
        console.error(
          "EbmDataEditorModal: Error fetching EBM metadata:",
          error
        );
        console.error("EbmDataEditorModal: Error details:", {
          message: error.message,
          status: error.response?.status,
          statusText: error.response?.statusText,
          data: error.response?.data,
        });
        this.isLoadingData = false;

        if (error.response?.status === 404) {
          console.warn(
            "EbmDataEditorModal: No EBM metadata found. Using default values."
          );
          this.$emit("warning", "No EBM metadata found. Using default values.");
          this.initializeFormDataWithEbmSettings();
          this.editableQuantity =
            this.fetchedPurchaseData?.quantity || this.formData.ebmPurchase.qty;
        } else {
          console.error(
            "EbmDataEditorModal: Failed to load EBM metadata. Using default values."
          );
          this.$emit(
            "error",
            "Failed to load EBM metadata. Using default values."
          );
          this.initializeFormDataWithEbmSettings();
          this.editableQuantity =
            this.fetchedPurchaseData?.quantity || this.formData.ebmPurchase.qty;
        }
      }
    },

    initializeFormDataWithMetadata(ebmSettings = null) {
      console.log("🔍 STEP 1: Starting initializeFormDataWithMetadata");
      console.log("📦 Inventory Item:", {
        hasProduct: !!this.inventoryItem?.product,
        productId: this.inventoryItem?.product?.id,
        productName: this.inventoryItem?.product?.name,
        productSku: this.inventoryItem?.product?.item_sku,
        price: this.inventoryItem?.price?.unit_price,
      });

      if (!this.inventoryItem || !this.inventoryItem.product) {
        console.log("❌ STEP 1 FAILED: Missing inventory item or product");
        return;
      }

      const product = this.inventoryItem.product;
      const price = this.inventoryItem.price;

      console.log("✅ STEP 1 COMPLETE: Inventory item validated");

      console.log("🔍 STEP 2: Extracting data from fetched responses");
      console.log("📊 Fetched Data Status:", {
        hasProductData: !!this.fetchedProductData,
        hasPurchaseData: !!this.fetchedPurchaseData,
        productDataKeys: this.fetchedProductData
          ? Object.keys(this.fetchedProductData)
          : [],
        purchaseDataKeys: this.fetchedPurchaseData
          ? Object.keys(this.fetchedPurchaseData)
          : [],
      });

      // Extract item data from multiple possible locations
      let productEbmData = {};
      let purchaseEbmData = {};

      // Try different data structure locations
      if (this.fetchedProductData?.rra_ebm_metadata?.itemList?.[0]) {
        productEbmData = this.fetchedProductData.rra_ebm_metadata.itemList[0];
        console.log("✅ Found product data in: rra_ebm_metadata.itemList[0]");
      } else if (this.fetchedProductData?.rra_ebm_metadata) {
        productEbmData = this.fetchedProductData.rra_ebm_metadata;
        console.log("✅ Found product data in: rra_ebm_metadata");
      } else if (this.fetchedProductData?.itemList?.[0]) {
        productEbmData = this.fetchedProductData.itemList[0];
        console.log("✅ Found product data in: itemList[0]");
      } else if (this.fetchedProductData) {
        productEbmData = this.fetchedProductData;
        console.log("✅ Found product data in: root");
      } else {
        console.log("❌ No product data found");
      }

      // Try different purchase data structure locations
      if (this.fetchedPurchaseData?.rra_ebm_metadata) {
        purchaseEbmData = this.fetchedPurchaseData.rra_ebm_metadata;
        console.log("✅ Found purchase data in: rra_ebm_metadata");
      } else if (this.fetchedPurchaseData?.itemList?.[0]) {
        purchaseEbmData = this.fetchedPurchaseData.itemList[0];
        console.log("✅ Found purchase data in: itemList[0]");
      } else if (this.fetchedPurchaseData) {
        purchaseEbmData = this.fetchedPurchaseData;
        console.log("✅ Found purchase data in: root");
      } else {
        console.log("❌ No purchase data found");
      }

      console.log("🔍 STEP 3: Analyzing extracted data");

      // Use purchase data as fallback for item fields if product data is empty
      const itemData =
        Object.keys(productEbmData).length > 0
          ? productEbmData
          : purchaseEbmData;

      console.log("🔍 STEP 4: Selecting data source");
      console.log("🎯 Selected Data Source:", {
        source:
          Object.keys(productEbmData).length > 0
            ? "productEbmData"
            : "purchaseEbmData",
        itemDataKeys: Object.keys(itemData),
        itemDataSample: {
          tin: itemData.tin,
          itemCd: itemData.itemCd,
          itemNm: itemData.itemNm,
          prc: itemData.prc,
          dftPrc: itemData.dftPrc,
        },
      });

      console.log("🔍 STEP 5: Initializing EBM Item form data", {
        productEbmData,
        purchaseEbmData,
        itemData,
      });

      // Initialize EBM Item data with fetched metadata
      this.formData.ebmItem = {
        tin: ebmSettings?.tin || "",
        bhfId: ebmSettings?.bhfId || "",
        itemCd:
          itemData.itemCd || purchaseEbmData.itemCd || product?.item_sku || "",
        itemClsCd:
          itemData.itemClsCd || purchaseEbmData.itemClsCd || "5020230602",
        itemTyCd: itemData.itemTyCd || purchaseEbmData.itemTyCd || "2",
        itemNm: itemData.itemNm || product?.name || "",
        orgnNatCd: itemData.orgnNatCd || purchaseEbmData.orgnNatCd || "RW",
        pkgUnitCd: itemData.pkgUnitCd || purchaseEbmData.pkgUnitCd || "NT",
        qtyUnitCd:
          itemData.qtyUnitCd ||
          purchaseEbmData.qtyUnitCd ||
          product?.quantity_unit ||
          "U",
        taxTyCd: itemData.taxTyCd || purchaseEbmData.taxTyCd || "B",
        isrcAplcbYn: itemData.isrcAplcbYn || purchaseEbmData.isrcAplcbYn || "N",
        useYn: itemData.useYn || purchaseEbmData.useYn || "Y",
        dftPrc: this.parseNumber(itemData.dftPrc) || price?.unit_price || 0,
        itemStdNm: productEbmData.itemStdNm || product?.name || null,
        btchNo: productEbmData.btchNo || null,
        bcd: productEbmData.bcd || product?.barcode || null,
        addInfo: productEbmData.addInfo || product?.description || null,
        grpPrcL1: productEbmData.grpPrcL1 || price?.unit_price || 0,
        grpPrcL2: productEbmData.grpPrcL2 || price?.unit_price || 0,
        grpPrcL3: productEbmData.grpPrcL3 || price?.unit_price || 0,
        grpPrcL4: productEbmData.grpPrcL4 || price?.unit_price || 0,
        grpPrcL5: productEbmData.grpPrcL5 || price?.unit_price || 0,
        sftyQty: productEbmData.sftyQty || 0,
        rraModYn: productEbmData.rraModYn || "N",
        regBhfId: productEbmData.regBhfId || null,
        regrNm: productEbmData.regrNm || "Admin",
        regrId: productEbmData.regrId || "Admin",
        modrNm: productEbmData.modrNm || "Admin",
        modrId: productEbmData.modrId || "Admin",
      };

      console.log("✅ STEP 5 COMPLETE: EBM Item form data initialized");
      console.log("📝 AFTER: Updated formData.ebmItem:", {
        tin: this.formData.ebmItem.tin,
        bhfId: this.formData.ebmItem.bhfId,
        itemCd: this.formData.ebmItem.itemCd,
        itemClsCd: this.formData.ebmItem.itemClsCd,
        itemNm: this.formData.ebmItem.itemNm,
        dftPrc: this.formData.ebmItem.dftPrc,
        priceSource: {
          itemDataPrc: itemData.prc,
          itemDataDftPrc: itemData.dftPrc,
          purchaseDataPrc: purchaseEbmData.prc,
          purchaseDataDftPrc: purchaseEbmData.dftPrc,
          inventoryPrice: price?.unit_price,
        },
      });

      console.log("🔍 STEP 6: Initializing EBM Purchase form data");
      console.log("📝 BEFORE: Current formData.ebmPurchase:", {
        tin: this.formData.ebmPurchase?.tin,
        invcNo: this.formData.ebmPurchase?.invcNo,
        pchsDt: this.formData.ebmPurchase?.pchsDt,
        totAmt: this.formData.ebmPurchase?.totAmt,
        itemListLength: this.formData.ebmPurchase?.itemList?.length,
      });

      // Initialize EBM Purchase data with fetched metadata
      this.formData.ebmPurchase = {
        tin: purchaseEbmData.tin || ebmSettings?.tin || "",
        bhfId: purchaseEbmData.bhfId || ebmSettings?.bhfId || "",
        invcNo: purchaseEbmData.invcNo || 0,
        orgInvcNo: purchaseEbmData.orgInvcNo || 0,
        regTyCd: purchaseEbmData.regTyCd || "A",
        pchsTyCd: purchaseEbmData.pchsTyCd || "N",
        rcptTyCd: purchaseEbmData.rcptTyCd || "P",
        pmtTyCd: purchaseEbmData.pmtTyCd || "01",
        pchsSttsCd: purchaseEbmData.pchsSttsCd || "02",
        pchsDt:
          purchaseEbmData.pchsDt ||
          new Date().toISOString().slice(0, 10).replace(/-/g, ""),
        totItemCnt: purchaseEbmData.totItemCnt || 1,
        taxblAmtA: this.parseNumber(purchaseEbmData.taxblAmtA) || 0,
        taxblAmtB:
          this.parseNumber(purchaseEbmData.taxblAmtB) || price?.unit_price || 0,
        taxblAmtC: this.parseNumber(purchaseEbmData.taxblAmtC) || 0,
        taxblAmtD: this.parseNumber(purchaseEbmData.taxblAmtD) || 0,
        taxRtA: purchaseEbmData.taxRtA || 0,
        taxRtB: purchaseEbmData.taxRtB || 18,
        taxRtC: purchaseEbmData.taxRtC || 0,
        taxRtD: purchaseEbmData.taxRtD || 0,
        taxAmtA: this.parseNumber(purchaseEbmData.taxAmtA) || 0,
        taxAmtB:
          this.parseNumber(purchaseEbmData.taxAmtB) ||
          EbmDataUtils.calculateTaxAmount(
            (purchaseEbmData.qty || 1) * (price?.unit_price || 0),
            18
          ),
        taxAmtC: this.parseNumber(purchaseEbmData.taxAmtC) || 0,
        taxAmtD: this.parseNumber(purchaseEbmData.taxAmtD) || 0,
        totTaxblAmt:
          this.parseNumber(purchaseEbmData.totTaxblAmt) ||
          (purchaseEbmData.qty || 1) * (price?.unit_price || 0),
        totTaxAmt:
          this.parseNumber(purchaseEbmData.totTaxAmt) ||
          EbmDataUtils.calculateTaxAmount(
            (purchaseEbmData.qty || 1) * (price?.unit_price || 0),
            18
          ),
        totAmt:
          this.parseNumber(purchaseEbmData.totAmt) ||
          (purchaseEbmData.qty || 1) * (price?.unit_price || 0) +
            EbmDataUtils.calculateTaxAmount(
              (purchaseEbmData.qty || 1) * (price?.unit_price || 0),
              18
            ),
        regrNm: purchaseEbmData.regrNm || "Admin",
        regrId: purchaseEbmData.regrId || "Admin",
        modrNm: purchaseEbmData.modrNm || "Admin",
        modrId: purchaseEbmData.modrId || "Admin",
        spplrTin: purchaseEbmData.spplrTin || "999999999",
        spplrBhfId: purchaseEbmData.spplrBhfId || "00",
        spplrNm: purchaseEbmData.spplrNm || "Supplier",
        spplrInvcNo: purchaseEbmData.spplrInvcNo || 1,
        spplrSdcId: purchaseEbmData.spplrSdcId || null,
        spplrItemCd: purchaseEbmData.spplrItemCd || null,
        cfmDt: purchaseEbmData.cfmDt || null,
        wrhsDt: purchaseEbmData.wrhsDt || null,
        cnclReqDt: purchaseEbmData.cnclReqDt || null,
        cnclDt: purchaseEbmData.cnclDt || null,
        rfdDt: purchaseEbmData.rfdDt || null,
        remark: purchaseEbmData.remark || null,
        itemList: purchaseEbmData.itemList || [],
        // item
        prc: this.fetchedPurchaseData?.unit_price || 0,
        qty:
          this.correctedPurchaseQuantity ||
          this.fetchedPurchaseData?.quantity ||
          0,
      };

      console.log("✅ STEP 6 COMPLETE: EBM Purchase form data initialized");
      console.log("📝 AFTER: Updated formData.ebmPurchase:", {
        tin: this.formData.ebmPurchase.tin,
        bhfId: this.formData.ebmPurchase.bhfId,
        invcNo: this.formData.ebmPurchase.invcNo,
        pchsDt: this.formData.ebmPurchase.pchsDt,
        pchsDtLength: this.formData.ebmPurchase.pchsDt?.length,
        pchsDtSource: {
          originalValue: purchaseEbmData.pchsDt,
          originalLength: purchaseEbmData.pchsDt?.length,
          finalValue: this.formData.ebmPurchase.pchsDt,
          finalLength: this.formData.ebmPurchase.pchsDt?.length,
        },
        totAmt: this.formData.ebmPurchase.totAmt,
        itemListLength: this.formData.ebmPurchase.itemList?.length,
        itemListSource: {
          purchaseDataItemList: purchaseEbmData.itemList?.length,
          hasItemList: !!purchaseEbmData.itemList,
        },
      });

      console.log("🔍 STEP 7: Ensuring itemList is populated");
      console.log("📋 BEFORE itemList fix:", {
        itemListExists: !!this.formData.ebmPurchase.itemList,
        itemListLength: this.formData.ebmPurchase.itemList?.length,
        itemListEmpty: this.formData.ebmPurchase.itemList?.length === 0,
      });

      // Ensure itemList is populated with the current item data
      if (
        !this.formData.ebmPurchase.itemList ||
        this.formData.ebmPurchase.itemList.length === 0
      ) {
        this.formData.ebmPurchase.itemList = [this.formData.ebmItem];
        console.log("✅ STEP 7: Added itemList with current ebmItem");
      } else {
        console.log("✅ STEP 7: itemList already populated");
      }

      console.log("📋 AFTER itemList fix:", {
        itemListLength: this.formData.ebmPurchase.itemList?.length,
        firstItemSample: this.formData.ebmPurchase.itemList?.[0]
          ? {
              tin: this.formData.ebmPurchase.itemList[0].tin,
              itemCd: this.formData.ebmPurchase.itemList[0].itemCd,
              itemNm: this.formData.ebmPurchase.itemList[0].itemNm,
              dftPrc: this.formData.ebmPurchase.itemList[0].dftPrc,
            }
          : null,
      });

      this.originalData = JSON.parse(JSON.stringify(this.formData));

      console.log("🎉 FINAL STEP: Form data initialization complete");

      // Trigger initial recalculation to ensure correct tax amounts
      this.$nextTick(() => {
        this.recalculateTaxAmountsForTaxType(this.formData.ebmItem.taxTyCd);
      });
    },

    initializeFormData() {
      if (!this.inventoryItem || !this.inventoryItem.product) return;

      const product = this.inventoryItem.product;
      const price = this.inventoryItem.price;

      // Initialize EBM Item data from existing metadata or defaults
      this.formData.ebmItem = {
        tin: "", // Will be populated from EBM settings
        bhfId: "", // Will be populated from EBM settings
        itemCd: product?.rra_ebm_metadata?.itemCd || product?.item_sku || "",
        itemClsCd: product?.rra_ebm_metadata?.itemClsCd || "5020230602",
        itemTyCd: "2", // Finished Good
        itemNm: product?.name || "",
        orgnNatCd: "RW",
        pkgUnitCd: "NT",
        qtyUnitCd: product?.quantity_unit || "U",
        taxTyCd: product?.rra_ebm_metadata?.taxTyCd || "B",
        isrcAplcbYn: "N",
        useYn: "Y",
        dftPrc: price?.unit_price || 0,
        itemStdNm: product?.name || null,
        btchNo: null,
        bcd: product?.barcode || null,
        addInfo: product?.description || null,
        grpPrcL1: price?.unit_price || 0,
        grpPrcL2: price?.unit_price || 0,
        grpPrcL3: price?.unit_price || 0,
        grpPrcL4: price?.unit_price || 0,
        grpPrcL5: price?.unit_price || 0,
        sftyQty: 0,
        rraModYn: "N",
        regBhfId: null,
        regrNm: "",
        regrId: "",
        modrNm: "",
        modrId: "",
      };

      // Initialize EBM Purchase data
      this.formData.ebmPurchase = {
        tin: "", // Will be populated from EBM settings
        bhfId: "", // Will be populated from EBM settings
        invcNo: 0,
        orgInvcNo: 0,
        regTyCd: "A",
        pchsTyCd: "N",
        rcptTyCd: "P",
        pmtTyCd: "01",
        pchsSttsCd: "02",
        pchsDt: new Date().toISOString().slice(0, 10).replace(/-/g, ""),
        totItemCnt: 1,
        taxblAmtA: 0,
        taxblAmtB: price?.unit_price || 0,
        taxblAmtC: 0,
        taxblAmtD: 0,
        taxRtA: 0,
        taxRtB: 18,
        taxRtC: 0,
        taxRtD: 0,
        taxAmtA: 0,
        taxAmtB: EbmDataUtils.calculateTaxAmount(
          (this.formData.ebmPurchase.qty || 1) *
            (this.formData.ebmPurchase.prc || 0),
          18
        ),
        taxAmtC: 0,
        taxAmtD: 0,
        totTaxblAmt:
          (this.formData.ebmPurchase.qty || 1) *
          (this.formData.ebmPurchase.prc || 0),
        totTaxAmt: EbmDataUtils.calculateTaxAmount(
          (this.formData.ebmPurchase.qty || 1) *
            (this.formData.ebmPurchase.prc || 0),
          18
        ),
        totAmt:
          (this.formData.ebmPurchase.qty || 1) *
            (this.formData.ebmPurchase.prc || 0) +
          EbmDataUtils.calculateTaxAmount(
            (this.formData.ebmPurchase.qty || 1) *
              (this.formData.ebmPurchase.prc || 0),
            18
          ),
        regrNm: "Admin",
        regrId: "Admin",
        modrNm: "Admin",
        modrId: "Admin",
        spplrTin: "999999999",
        spplrBhfId: "00",
        spplrNm: "Supplier",
        spplrInvcNo: 1,
        spplrSdcId: null,
        spplrItemCd: null,
        cfmDt: null,
        wrhsDt: null,
        cnclReqDt: null,
        cnclDt: null,
        rfdDt: null,
        remark: null,
        itemList: [],
      };

      // Store original data for comparison
      this.originalData = JSON.parse(JSON.stringify(this.formData));

      // Data initialized with metadata - validation will run on save
      console.log(
        "EbmDataEditorModal: Data initialized with metadata, validation will run on save"
      );
    },

    initializeFormDataWithEbmSettings() {
      if (!this.inventoryItem || !this.inventoryItem.product) {
        console.warn(
          "EbmDataEditorModal: Cannot initialize - missing inventory item or product"
        );
        return;
      }

      const product = this.inventoryItem.product;
      const price = this.inventoryItem.price;
      const ebmSettings = this.$store.getters.rraEbmSettings;

      console.log(
        "EbmDataEditorModal: Initializing form data with EBM settings",
        {
          product: {
            id: product.id,
            name: product.name,
            item_sku: product.item_sku,
            quantity_unit: product.quantity_unit,
            barcode: product.barcode,
            description: product.description,
          },
          price: {
            unit_price: price?.unit_price,
          },
          ebmSettings: {
            tin: ebmSettings?.tin,
            bhfId: ebmSettings?.bhfId,
            ebm_activated: ebmSettings?.ebm_activated,
          },
        }
      );

      // Initialize EBM Item data with EBM settings

      this.formData.ebmItem = {
        tin: ebmSettings?.tin || "",
        bhfId: ebmSettings?.bhfId || "",
        itemCd: product?.item_sku || "",
        itemClsCd: "5020230602",
        itemTyCd: "2",
        itemNm: product?.name || "",
        orgnNatCd: "RW",
        pkgUnitCd: "NT",
        qtyUnitCd: product?.quantity_unit || "U",
        taxTyCd: "B",
        isrcAplcbYn: "N",
        useYn: "Y",
        dftPrc: price?.unit_price || 0,
        itemStdNm: product?.name || null,
        btchNo: null,
        bcd: product?.barcode || null,
        addInfo: product?.description || null,
        grpPrcL1: price?.unit_price || 0,
        grpPrcL2: price?.unit_price || 0,
        grpPrcL3: price?.unit_price || 0,
        grpPrcL4: price?.unit_price || 0,
        grpPrcL5: price?.unit_price || 0,
        sftyQty: 0,
        rraModYn: "N",
        regBhfId: null,
        regrNm: "",
        regrId: "",
        modrNm: "",
        modrId: "",
      };

      // Get default quantity from fetched purchase data or use 1
      const defaultQuantity = this.fetchedPurchaseData?.quantity || 1;

      // Initialize EBM Purchase data with EBM settings
      this.formData.ebmPurchase = {
        tin: ebmSettings?.tin || "",
        bhfId: ebmSettings?.bhfId || "",
        invcNo: 0,
        orgInvcNo: 0,
        regTyCd: "A",
        pchsTyCd: "N",
        rcptTyCd: "P",
        pmtTyCd: "01",
        pchsSttsCd: "02",
        pchsDt: new Date().toISOString().slice(0, 10).replace(/-/g, ""),
        totItemCnt: 1, // Always 1 as it represents the count of items in itemList
        taxblAmtA: 0,
        taxblAmtB: price?.unit_price || 0,
        taxblAmtC: 0,
        taxblAmtD: 0,
        taxRtA: 0,
        taxRtB: 18,
        taxRtC: 0,
        taxRtD: 0,
        taxAmtA: 0,
        taxAmtB: EbmDataUtils.calculateTaxAmount(
          defaultQuantity * (this.formData.ebmPurchase.prc || 0),
          18
        ),
        taxAmtC: 0,
        taxAmtD: 0,
        totTaxblAmt: defaultQuantity * (this.formData.ebmPurchase.prc || 0),
        totTaxAmt: EbmDataUtils.calculateTaxAmount(
          defaultQuantity * (this.formData.ebmPurchase.prc || 0),
          18
        ),
        totAmt:
          defaultQuantity * (this.formData.ebmPurchase.prc || 0) +
          EbmDataUtils.calculateTaxAmount(
            defaultQuantity * (this.formData.ebmPurchase.prc || 0),
            18
          ),
        regrNm: "",
        regrId: "",
        modrNm: "",
        modrId: "",
        spplrTin: "",
        spplrBhfId: "",
        spplrNm: "",
        spplrInvcNo: 1,
        spplrSdcId: null,
        spplrItemCd: null,
        cfmDt: null,
        wrhsDt: null,
        cnclReqDt: null,
        cnclDt: null,
        rfdDt: null,
        remark: null,
        itemList: [],
        // For the one item
        prc: price?.unit_price || 0,
        qty: defaultQuantity,
      };

      // Store original data for comparison
      this.originalData = JSON.parse(JSON.stringify(this.formData));

      // Data initialized with EBM settings - validation will run on save
      console.log(
        "EbmDataEditorModal: Data initialized with EBM settings, validation will run on save"
      );

      // Initialize editable quantity with the fetched purchase data quantity
      this.editableQuantity =
        this.fetchedPurchaseData?.quantity || this.formData.ebmPurchase.qty;

      // Trigger initial recalculation to ensure correct tax amounts
      this.$nextTick(() => {
        this.recalculateTaxAmountsForTaxType(this.formData.ebmItem.taxTyCd);
      });
    },

    validateForm(forceValidation = false) {
      console.log("EbmDataEditorModal: validateForm called", {
        forceValidation,
        isDataInitialized: this.isDataInitialized,
        isLoadingData: this.isLoadingData,
        hasInventoryItem: !!this.inventoryItem?.product?.id,
        fetchedProductData: this.fetchedProductData !== null,
        originalData: this.originalData !== null,
      });

      // Always clear previous errors
      this.validationErrors = [];

      // Only validate if explicitly forced (e.g., from handleSave)
      if (forceValidation) {
        console.log(
          "EbmDataEditorModal: Running validation - explicitly forced"
        );

        try {
          // Validate EBM Item data
          console.log("EbmDataEditorModal: Validating EBM Item data");
          const itemErrors = ItemSaveReqValidator.validate(
            this.formData.ebmItem,
            { hasExistingEbmMetadata: this.hasExistingEbmMetadata }
          );
          this.validationErrors.push(...itemErrors);

          // Validate EBM Purchase data
          console.log("EbmDataEditorModal: Validating EBM Purchase data");
          const purchaseErrors = TrnsPurchaseSaveReqValidator.validate(
            this.formData.ebmPurchase
          );
          this.validationErrors.push(...purchaseErrors);

          // Note: product_purchase_id validation removed - will save product only if not available

          console.log(
            "EbmDataEditorModal: Validation completed. Errors:",
            this.validationErrors.length,
            this.validationErrors
          );
        } catch (error) {
          console.error("EbmDataEditorModal: Error during validation:", error);
          // Add a generic validation error if something goes wrong
          this.validationErrors.push({
            field: "general",
            message:
              "Validation failed due to an error. Please check your data.",
          });
        }
      } else {
        console.log(
          "EbmDataEditorModal: Skipping validation - not explicitly forced"
        );
      }
    },

    hasFieldError(fieldPath) {
      return this.validationErrors.some((error) => error.field === fieldPath);
    },

    // Helper methods for data parsing and formatting
    parseNumber(value) {
      if (value === null || value === undefined || value === "") return 0;
      const parsed = parseFloat(value);
      return isNaN(parsed) ? 0 : parsed;
    },

    // Clear validation errors for a specific field
    clearFieldValidationError(fieldPath) {
      this.validationErrors = this.validationErrors.filter(
        (error) => error.field !== fieldPath
      );
    },

    // Extract error message from API response
    extractErrorMessage(error) {
      console.log("🔍 Extracting error message from:", error);

      // Try to get the nested error message structure
      const responseData = error.response?.data;
      if (responseData?.error?.message) {
        console.log(
          "✅ Found nested error message:",
          responseData.error.message
        );
        return responseData.error.message;
      }

      // Fallback to direct message
      if (responseData?.message) {
        console.log("✅ Found direct message:", responseData.message);
        return responseData.message;
      }

      // Fallback to error message
      if (error.message) {
        console.log("✅ Found error.message:", error.message);
        return error.message;
      }

      // Final fallback
      console.log("⚠️ Using fallback error message");
      return "An unexpected error occurred";
    },

    // Sanitize EBM data to ensure no null values are sent
    sanitizeEbmData(data, type = "item") {
      const sanitized = { ...data };

      if (type === "item") {
        // Fields valid for ItemSaveReq dataclass
        const stringFields = [
          "tin",
          "bhfId",
          "itemCd",
          "itemClsCd",
          "itemTyCd",
          "itemNm",
          "orgnNatCd",
          "pkgUnitCd",
          "qtyUnitCd",
          "taxTyCd",
          "isrcAplcbYn",
          "useYn",
          "itemStdNm",
          "btchNo",
          "bcd",
          "addInfo",
          "rraModYn",
          "regBhfId",
          "regrNm",
          "regrId",
          "modrNm",
          "modrId",
        ];

        const numericFields = [
          "dftPrc",
          "grpPrcL1",
          "grpPrcL2",
          "grpPrcL3",
          "grpPrcL4",
          "grpPrcL5",
          "sftyQty",
        ];

        // Sanitize string fields
        stringFields.forEach((field) => {
          if (
            sanitized[field] === null ||
            sanitized[field] === undefined ||
            sanitized[field] === ""
          ) {
            if (field === "regrNm" || field === "modrNm") {
              sanitized[field] = "Admin";
            } else if (field === "regrId" || field === "modrId") {
              sanitized[field] = "Admin";
            } else {
              sanitized[field] = "";
            }
          }
        });

        // Sanitize numeric fields
        numericFields.forEach((field) => {
          if (
            sanitized[field] === null ||
            sanitized[field] === undefined ||
            sanitized[field] === ""
          ) {
            sanitized[field] = 0;
          } else {
            sanitized[field] = this.parseNumber(sanitized[field]);
          }
        });
      } else if (type === "purchase") {
        // Fields valid for purchase EBM metadata
        const stringFields = [
          "tin",
          "bhfId",
          "regrNm",
          "regrId",
          "modrNm",
          "modrId",
          "spplrTin",
          "spplrBhfId",
          "spplrNm",
          "regTyCd",
          "pchsTyCd",
          "pmtTyCd",
          "pchsSttsCd",
          "rctTyCd",
          "pchsDt",
          "cfmDt",
          "wrhsDt",
          "cnclReqDt",
          "cnclDt",
          "rfdDt",
          "remark",
          "spplrItemCd",
          "spplrSdcId",
        ];

        const numericFields = [
          "invcNo",
          "orgInvcNo",
          "totItemCnt",
          "qty",
          "prc",
          "taxblAmtA",
          "taxblAmtB",
          "taxblAmtC",
          "taxblAmtD",
          "taxRtA",
          "taxRtB",
          "taxRtC",
          "taxRtD",
          "taxAmtA",
          "taxAmtB",
          "taxAmtC",
          "taxAmtD",
          "totTaxblAmt",
          "totTaxAmt",
          "totAmt",
          "spplrInvcNo",
          "spplrSdcId",
        ];

        // Sanitize string fields
        stringFields.forEach((field) => {
          if (
            sanitized[field] === null ||
            sanitized[field] === undefined ||
            sanitized[field] === ""
          ) {
            if (field === "regrNm" || field === "modrNm") {
              sanitized[field] = "Admin";
            } else if (field === "regrId" || field === "modrId") {
              sanitized[field] = "Admin";
            } else if (field === "spplrTin") {
              sanitized[field] = "999999999";
            } else if (field === "spplrBhfId") {
              sanitized[field] = "00";
            } else if (field === "spplrNm") {
              sanitized[field] = "Supplier";
            } else {
              sanitized[field] = "";
            }
          }
        });

        // Sanitize numeric fields
        numericFields.forEach((field) => {
          if (
            sanitized[field] === null ||
            sanitized[field] === undefined ||
            sanitized[field] === ""
          ) {
            sanitized[field] = 0;
          } else {
            sanitized[field] = this.parseNumber(sanitized[field]);
          }
        });
      }

      console.log(`🧹 Sanitized EBM ${type} data:`, sanitized);
      return sanitized;
    },

    parseEbmDate(dateString) {
      if (!dateString) return "";

      // If already in EBM format (YYYYMMDD), return as is
      if (dateString.length === 8 && /^\d{8}$/.test(dateString)) {
        return dateString;
      }

      // If in HTML date format (YYYY-MM-DD), convert to EBM format
      if (dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
        return dateString.replace(/-/g, "");
      }

      // If in ISO format, extract date part and convert
      if (dateString.match(/^\d{4}-\d{2}-\d{2}T/)) {
        return dateString.substring(0, 10).replace(/-/g, "");
      }

      // Try to parse as Date and convert to EBM format
      try {
        const date = new Date(dateString);
        if (!isNaN(date.getTime())) {
          const year = date.getFullYear();
          const month = String(date.getMonth() + 1).padStart(2, "0");
          const day = String(date.getDate()).padStart(2, "0");
          return `${year}${month}${day}`;
        }
      } catch (error) {
        console.warn(
          "EbmDataEditorModal: Could not parse date:",
          dateString,
          error
        );
      }

      // Fallback: return current date in EBM format
      const now = new Date();
      const year = now.getFullYear();
      const month = String(now.getMonth() + 1).padStart(2, "0");
      const day = String(now.getDate()).padStart(2, "0");
      return `${year}${month}${day}`;
    },

    parseDate(dateString) {
      if (!dateString) return "";
      // Handle YYYYMMDD format
      if (dateString.length === 8) {
        const year = dateString.substring(0, 4);
        const month = dateString.substring(4, 6);
        const day = dateString.substring(6, 8);
        return `${year}-${month}-${day}`;
      }
      // Handle YYYYMMDDHHMMSS format
      if (dateString.length === 14) {
        const year = dateString.substring(0, 4);
        const month = dateString.substring(4, 6);
        const day = dateString.substring(6, 8);
        const hour = dateString.substring(8, 10);
        const minute = dateString.substring(10, 12);
        const second = dateString.substring(12, 14);
        return `${year}-${month}-${day}T${hour}:${minute}:${second}`;
      }
      return dateString;
    },

    // Helper methods for preview formatting
    getTaxTypeLabel(taxType) {
      const types = {
        A: "A - Exempt",
        B: "B - 18% VAT",
        C: "C - 0% VAT",
        D: "D - Special",
      };
      return types[taxType] || taxType || "Not specified";
    },

    getQuantityUnitLabel(unit) {
      const units = {
        U: "U - Unit",
        KG: "KG - Kilogram",
        L: "L - Liter",
        M: "M - Meter",
      };
      return units[unit] || unit || "Not specified";
    },

    // Set purchase quantity to original value
    useOriginalQuantity() {
      const newQuantity = this.originalPurchaseQuantity;
      const previousQuantity = this.inventoryItem.quantity;

      // Update quantity in form data
      // Note: totItemCnt should remain 1 as it represents the count of items in itemList, not the quantity of the item
      this.formData.ebmPurchase.qty = newQuantity;

      // Update editableQuantity to sync with UI
      this.editableQuantity = newQuantity;

      // Recalculate amounts based on quantity change
      this.recalculateAmountsForQuantityChange(previousQuantity, newQuantity);

      // Recalculate tax amounts based on current tax type
      this.recalculateTaxAmountsForTaxType(this.formData.ebmItem.taxTyCd);
    },
    // Set purchase quantity to corrected value (inventory)
    useCorrectedQuantity() {
      const newQuantity = this.correctedPurchaseQuantity;
      const previousQuantity = this.fetchedPurchaseData?.quantity;

      // Update the form data with the corrected quantity
      // Note: totItemCnt should remain 1 as it represents the count of items in itemList, not the quantity of the item
      this.formData.ebmPurchase.qty = newQuantity;

      // Update editableQuantity to sync with UI
      this.editableQuantity = newQuantity;

      // Recalculate amounts based on quantity change
      this.recalculateAmountsForQuantityChange(previousQuantity, newQuantity);

      // Recalculate tax amounts based on current tax type
      this.recalculateTaxAmountsForTaxType(this.formData.ebmItem.taxTyCd);
    },

    // Recalculate amounts when quantity changes
    recalculateAmountsForQuantityChange(previousQuantity, newQuantity) {
      const prevQty = this.parseNumber(previousQuantity);
      const newQty = this.parseNumber(newQuantity);

      if (prevQty === 0 || newQty === 0) {
        console.log("⚠️ Cannot recalculate with zero quantity");
        return;
      }

      const quantityRatio = newQty / prevQty;
      console.log("📊 Recalculating amounts with ratio:", quantityRatio);

      // Note: Tax amounts are now handled separately by tax type logic
      // This method only handles non-tax related amount scaling if needed
      console.log(
        "📊 Quantity changed - tax amounts will be recalculated separately based on tax type"
      );

      // Totals will be recalculated by the tax type recalculation method
      console.log(
        "✅ Quantity change processed - totals will be updated by tax type logic"
      );
    },

    // Recalculate totals when individual amounts change
    recalculateTotals() {
      const taxableAmounts = [
        "taxblAmtA",
        "taxblAmtB",
        "taxblAmtC",
        "taxblAmtD",
      ];
      const taxAmounts = ["taxAmtA", "taxAmtB", "taxAmtC", "taxAmtD"];

      const totalTaxableAmount = taxableAmounts.reduce((sum, field) => {
        return sum + this.parseNumber(this.formData.ebmPurchase[field]);
      }, 0);

      const totalTaxAmount = taxAmounts.reduce((sum, field) => {
        return sum + this.parseNumber(this.formData.ebmPurchase[field]);
      }, 0);

      const totalAmount = totalTaxableAmount + totalTaxAmount;

      this.formData.ebmPurchase.totTaxblAmt =
        Math.round(totalTaxableAmount * 100) / 100;
      this.formData.ebmPurchase.totTaxAmt =
        Math.round(totalTaxAmount * 100) / 100;
      this.formData.ebmPurchase.totAmt = Math.round(totalAmount * 100) / 100;

      console.log("🔄 Totals recalculated:", {
        totalTaxableAmount,
        totalTaxAmount,
        totalAmount,
      });
    },

    // Recalculate tax amounts based on tax type code
    recalculateTaxAmountsForTaxType(taxType) {
      console.log("🧮 Recalculating tax amounts for tax type:", taxType);

      if (taxType === "B") {
        // Tax Type B: Calculate taxable amounts based on quantity and unit price
        this.calculateTaxableAmountsForTypeB();
      } else {
        // Non-B tax types: Set taxable amounts to zero
        this.resetTaxableAmountsToZero();
      }

      // Always recalculate totals after tax type change
      this.recalculateTotals();
    },

    // Calculate fields based on Total Amount input
    calculateFieldsFromTotalAmount() {
      console.log("💰 Calculating fields from Total Amount input");

      const totalAmount = this.parseNumber(this.formData.ebmPurchase.totAmt);
      const quantity = this.parseNumber(this.formData.ebmPurchase.qty);
      const taxType = this.formData.ebmItem.taxTyCd;

      if (totalAmount <= 0 || quantity <= 0) {
        console.log(
          "⚠️ Invalid total amount or quantity, skipping calculation"
        );
        return;
      }

      console.log("📊 Input values:", { totalAmount, quantity, taxType });

      if (taxType === "B") {
        // For Tax Type B: Calculate taxable amount and tax amount
        // Total Amount = Taxable Amount + Tax Amount
        // Tax Amount = Taxable Amount × Tax Rate (18%)
        // So: Total Amount = Taxable Amount + (Taxable Amount × 0.18)
        // Total Amount = Taxable Amount × (1 + 0.18) = Taxable Amount × 1.18
        // Taxable Amount = Total Amount / 1.18

        const taxableAmount = totalAmount / 1.18; // 18% tax rate
        const taxAmount = totalAmount - taxableAmount;

        console.log("🧮 Calculated amounts:", { taxableAmount, taxAmount });

        // Update the fields only if they haven't been user-edited
        if (!this.userEditedFields.has("taxblAmtB")) {
          this.formData.ebmPurchase.taxblAmtB =
            Math.round(taxableAmount * 100) / 100;
        }
        if (!this.userEditedFields.has("taxAmtB")) {
          this.formData.ebmPurchase.taxAmtB = Math.round(taxAmount * 100) / 100;
        }

        // Reset other tax amounts to zero for Tax Type B (only if not user-edited)
        if (!this.userEditedFields.has("taxblAmtA")) {
          this.formData.ebmPurchase.taxblAmtA = 0;
        }
        if (!this.userEditedFields.has("taxblAmtC")) {
          this.formData.ebmPurchase.taxblAmtC = 0;
        }
        if (!this.userEditedFields.has("taxblAmtD")) {
          this.formData.ebmPurchase.taxblAmtD = 0;
        }
        if (!this.userEditedFields.has("taxAmtA")) {
          this.formData.ebmPurchase.taxAmtA = 0;
        }
        if (!this.userEditedFields.has("taxAmtC")) {
          this.formData.ebmPurchase.taxAmtC = 0;
        }
        if (!this.userEditedFields.has("taxAmtD")) {
          this.formData.ebmPurchase.taxAmtD = 0;
        }

        // Update totals (always update these as they're calculated)
        this.formData.ebmPurchase.totTaxblAmt =
          Math.round(taxableAmount * 100) / 100;
        this.formData.ebmPurchase.totTaxAmt = Math.round(taxAmount * 100) / 100;

        console.log("✅ Updated fields:", {
          taxblAmtB: this.formData.ebmPurchase.taxblAmtB,
          taxAmtB: this.formData.ebmPurchase.taxAmtB,
          totTaxblAmt: this.formData.ebmPurchase.totTaxblAmt,
          totTaxAmt: this.formData.ebmPurchase.totTaxAmt,
          totAmt: this.formData.ebmPurchase.totAmt,
        });
      } else {
        // For non-B tax types: Set all tax amounts to zero
        this.resetTaxableAmountsToZero();
        this.formData.ebmPurchase.totTaxblAmt = 0;
        this.formData.ebmPurchase.totTaxAmt = 0;
        this.formData.ebmPurchase.totAmt = totalAmount;
      }
    },

    // Track user edits on tax fields
    trackUserEdit(fieldName) {
      console.log("👤 User edited field:", fieldName);
      this.userEditedFields.add(fieldName);
    },

    // Clear user edit tracking when tax type or quantity changes
    clearUserEditTracking() {
      console.log(
        "🔄 Clearing user edit tracking due to tax type or quantity change"
      );
      this.userEditedFields.clear();
    },

    // Calculate taxable amounts for Tax Type B
    calculateTaxableAmountsForTypeB() {
      const quantity = this.parseNumber(this.formData.ebmPurchase.qty);
      const unitPrice = this.parseNumber(this.formData.ebmPurchase.prc);
      console.log("💰 Calculating Tax Type B amounts:", {
        quantity,
        unitPrice,
      });
      const baseAmount = quantity * unitPrice;

      console.log("💰 Calculating Tax Type B amounts:", {
        quantity,
        unitPrice,
        baseAmount,
      });

      // Set Taxable Amount B to the calculated amount
      console.log(
        "EbmDataEditorModal: Initializing data with metadata",
        baseAmount,
        Math.round(baseAmount * 100) / 100
      );
      this.formData.ebmPurchase.taxblAmtB = Math.round(baseAmount * 100) / 100;

      // Calculate tax amount B (assuming 18% tax rate - you can make this configurable)
      const taxRateB = 0.18; // 18% tax rate
      const taxAmountB = baseAmount * taxRateB;
      this.formData.ebmPurchase.taxAmtB = Math.round(taxAmountB * 100) / 100;

      // Reset other taxable amounts to zero for Tax Type B
      this.formData.ebmPurchase.taxblAmtA = 0;
      this.formData.ebmPurchase.taxblAmtC = 0;
      this.formData.ebmPurchase.taxblAmtD = 0;
      this.formData.ebmPurchase.taxAmtA = 0;
      this.formData.ebmPurchase.taxAmtC = 0;
      this.formData.ebmPurchase.taxAmtD = 0;

      console.log("✅ Tax Type B amounts calculated:", {
        taxblAmtB: this.formData.ebmPurchase.taxblAmtB,
        taxAmtB: this.formData.ebmPurchase.taxAmtB,
      });
    },

    // Reset all taxable amounts to zero for non-B tax types
    resetTaxableAmountsToZero() {
      console.log("🔄 Resetting taxable amounts to zero for non-B tax type");

      const taxableAmounts = [
        "taxblAmtA",
        "taxblAmtB",
        "taxblAmtC",
        "taxblAmtD",
      ];
      const taxAmounts = ["taxAmtA", "taxAmtB", "taxAmtC", "taxAmtD"];

      taxableAmounts.forEach((field) => {
        this.formData.ebmPurchase[field] = 0;
      });

      taxAmounts.forEach((field) => {
        this.formData.ebmPurchase[field] = 0;
      });

      console.log("✅ All taxable amounts reset to zero");
    },

    getItemTypeLabel(type) {
      const types = {
        1: "1 - Raw Material",
        2: "2 - Finished Good",
        3: "3 - Service",
      };
      return types[type] || type || "Not specified";
    },

    getItemClassificationLabel(classification) {
      const classifications = {
        1: "1 - Food & Beverages",
        2: "2 - Electronics",
        3: "3 - Clothing & Textiles",
        4: "4 - Home & Garden",
        5: "5 - Health & Beauty",
        6: "6 - Automotive",
        7: "7 - Sports & Recreation",
        8: "8 - Books & Media",
        9: "9 - Office Supplies",
        10: "10 - Other",
      };
      return (
        classifications[classification] || classification || "Not specified"
      );
    },

    getPaymentTypeLabel(type) {
      const types = {
        C: "Cash",
        T: "Transfer",
        P: "POS",
        M: "Mobile Money",
      };
      return types[type] || type || "Not specified";
    },

    getPurchaseStatusLabel(status) {
      const statuses = {
        "01": "Pending",
        "02": "Completed",
        "03": "Cancelled",
        "04": "Refunded",
      };
      return statuses[status] || status || "Not specified";
    },

    formatPurchaseDate(dateString) {
      if (!dateString) return "Not specified";
      // Format YYYYMMDD to DD/MM/YYYY
      if (dateString.length === 8) {
        const year = dateString.substring(0, 4);
        const month = dateString.substring(4, 6);
        const day = dateString.substring(6, 8);
        return `${day}/${month}/${year}`;
      }
      return dateString;
    },

    async handleSave() {
      try {
        // Check if inventory item and product exist
        if (!this.inventoryItem || !this.inventoryItem.product) {
          console.error("Invalid inventory item data");
          this.$emit("error", "Invalid inventory item data");
          return;
        }

        // Validate quantity mismatch selection if there's a mismatch
        if (this.hasQuantityMismatch && !this.isPurchaseDataDisabled) {
          if (
            !this.selectedQuantityOptions ||
            this.selectedQuantityOptions.length === 0
          ) {
            this.$emit("error", {
              title: "Selection Required",
              message:
                "Please select a quantity option (Last Purchase or Current Inventory) before continuing.",
              type: "error",
            });
            return;
          }
        }

        // Handle different actions based on current tab
        if (this.activeTab === "product") {
          return this.handleContinue();
        } else if (this.activeTab === "purchase") {
          return this.handlePreview();
        } else if (this.activeTab === "preview") {
          return this.handleActualSave();
        } else {
          return this.handleActualSave();
        }
      } catch (error) {
        console.error("❌ Error in handleSave:", error);
        this.$emit("error", {
          title: "Save Failed",
          message: this.extractErrorMessage(error),
          type: "error",
        });
      }
    },

    async handleContinue() {
      try {
        // Always validate before continuing (force validation)
        console.log("🔍 Validating form before continue...");
        this.validateForm(true);

        // Show validation errors if any exist
        if (this.validationErrors.length > 0) {
          console.log("❌ Validation failed:", this.validationErrors);
          console.log(
            "📋 Validation errors:",
            this.validationErrors.map((e) => `${e.field}: ${e.message}`)
          );

          // Show toast notification for validation errors
          this.$emit("validation-error", {
            title: "Validation Failed",
            message: `Please fix ${
              this.validationErrors.length
            } validation error${
              this.validationErrors.length > 1 ? "s" : ""
            } before continuing.`,
            type: "error",
          });

          // Scroll to top to show validation errors
          this.$nextTick(() => {
            const modalContent = this.$el.querySelector(".modal-content");
            if (modalContent) {
              modalContent.scrollTop = 0;
            }
          });

          return;
        }

        // Check if purchase data is already saved (read-only mode)
        if (this.hasPurchaseEbmMetadata) {
          console.log(
            "✅ Validation passed, saving product only (purchase data already saved)..."
          );
          // Directly save the product since purchase data is already saved
          return this.performSaveLogic();
        }

        // Check if product_purchase_id is not available
        if (!this.hasProductPurchaseId) {
          console.log(
            "✅ Validation passed, saving product only (product_purchase_id not available)..."
          );
          // Directly save the product since purchase data is not available
          return this.performSaveLogic();
        }

        console.log("✅ Validation passed, switching to purchase tab...");

        // Switch to purchase tab
        this.activeTab = "purchase";

        // Scroll to top
        this.$nextTick(() => {
          const modalContent = this.$el.querySelector(".modal-content");
          if (modalContent) {
            modalContent.scrollTop = 0;
          }
        });
      } catch (error) {
        console.error("❌ Error in handleContinue:", error);
        this.$emit("error", {
          title: "Continue Failed",
          message: this.extractErrorMessage(error),
          type: "error",
        });
      }
    },

    async handlePreview() {
      try {
        // Validate Purchase Unit Price before previewing
        const prc = this.parseNumber(this.formData.ebmPurchase.prc);
        if (!prc || prc <= 0) {
          this.$emit("error", {
            title: "Purchase Unit Price Required",
            message:
              "Please enter a valid Purchase Unit Price before previewing.",
            type: "error",
          });
          return;
        }

        // Always validate before previewing (force validation)
        console.log("🔍 Validating form before preview...");
        this.validateForm(true);

        // Show validation errors if any exist
        if (this.validationErrors.length > 0) {
          console.log("❌ Validation failed:", this.validationErrors);
          console.log(
            "📋 Validation errors:",
            this.validationErrors.map((e) => `${e.field}: ${e.message}`)
          );

          // Show toast notification for validation errors
          this.$emit("validation-error", {
            title: "Validation Failed",
            message: `Please fix ${
              this.validationErrors.length
            } validation error${
              this.validationErrors.length > 1 ? "s" : ""
            } before previewing.`,
            type: "error",
          });

          // Scroll to top to show validation errors
          this.$nextTick(() => {
            const modalContent = this.$el.querySelector(".modal-content");
            if (modalContent) {
              modalContent.scrollTop = 0;
            }
          });

          return;
        }

        console.log("✅ Validation passed, switching to preview tab...");

        // Switch to preview tab
        this.activeTab = "preview";

        // Scroll to top
        this.$nextTick(() => {
          const modalContent = this.$el.querySelector(".modal-content");
          if (modalContent) {
            modalContent.scrollTop = 0;
          }
        });
      } catch (error) {
        console.error("❌ Error in handlePreview:", error);
        this.$emit("error", {
          title: "Preview Failed",
          message: this.extractErrorMessage(error),
          type: "error",
        });
      }
    },

    async handleActualSave() {
      try {
        // Always validate before saving (force validation)
        console.log("🔍 Validating form before save...");
        this.validateForm(true);

        // Show validation errors if any exist
        if (this.validationErrors.length > 0) {
          console.log("❌ Validation failed:", this.validationErrors);
          console.log(
            "📋 Validation errors:",
            this.validationErrors.map((e) => `${e.field}: ${e.message}`)
          );

          // Show toast notification for validation errors
          this.$emit("validation-error", {
            title: "Validation Failed",
            message: `Please fix ${
              this.validationErrors.length
            } validation error${
              this.validationErrors.length > 1 ? "s" : ""
            } before saving.`,
            type: "error",
          });

          // Scroll to top to show validation errors
          this.$nextTick(() => {
            const modalContent = this.$el.querySelector(".modal-content");
            if (modalContent) {
              modalContent.scrollTop = 0;
            }
          });

          return;
        }

        console.log("✅ Validation passed, proceeding with save...");

        // Call the original save logic (everything after validation)
        return this.performSaveLogic();
      } catch (error) {
        console.error("❌ Error in handleActualSave:", error);
        this.$emit("error", {
          title: "Save Failed",
          message: this.extractErrorMessage(error),
          type: "error",
        });
      }
    },

    async performSaveLogic() {
      this.$emit("loading", true);

      // Determine if we're saving purchase data or just product data
      const isSavingPurchaseData =
        this.hasProductPurchaseId && !this.hasPurchaseEbmMetadata;

      // Only validate purchase fields if we're actually saving purchase data
      if (isSavingPurchaseData) {
        // Validate prc and qty are numbers before purchase save operation
        const prc = this.parseNumber(this.formData.ebmPurchase.prc);
        const qty = this.parseNumber(this.formData.ebmPurchase.qty);

        if (isNaN(prc) || prc <= 0) {
          this.$emit("loading", false);
          this.$emit("error", {
            title: "Invalid Purchase Unit Price",
            message: "Purchase Unit Price must be a valid positive number.",
            type: "error",
          });
          return;
        }

        if (isNaN(qty) || qty <= 0) {
          this.$emit("loading", false);
          this.$emit("error", {
            title: "Invalid Quantity",
            message: "Quantity must be a valid positive number.",
            type: "error",
          });
          return;
        }

        console.log("✅ Purchase validation passed - prc:", prc, "qty:", qty);
      } else {
        console.log("✅ Product-only save - skipping purchase validation");
      }

      // Prepare EBM data for submission
      const ebmItemData = this.sanitizeEbmData(this.formData.ebmItem, "item");
      const ebmPurchaseData = this.sanitizeEbmData(
        this.formData.ebmPurchase,
        "purchase"
      );

      console.log("🔍 Save Logic Analysis:", {
        hasExistingEbmMetadata: this.hasExistingEbmMetadata,
        hasPurchaseEbmMetadata: this.hasPurchaseEbmMetadata,
        hasExistingPurchase: !!this.fetchedPurchaseData,
        productId: this.inventoryItem.product?.id,
        hasProductPurchaseId: this.hasProductPurchaseId,
      });

      // Continue with original save logic...
      try {
        // Business Logic Implementation:
        // 1. Always update the product with EBM metadata (both cases)
        const productData = {
          product_id: this.inventoryItem.product?.id,
          name: ebmItemData.itemNm || this.inventoryItem.product?.name,
          item_sku: this.inventoryItem.product?.item_sku,
          barcode: ebmItemData.bcd || this.inventoryItem.product?.barcode,
          description: this.inventoryItem.product?.description,
          default_quantity_unit:
            this.inventoryItem.product?.quantity_unit ||
            this.inventoryItem.product?.default_quantity_unit,
          rra_ebm_metadata: {
            // Basic identification fields
            tin: ebmItemData.tin,
            bhfId: ebmItemData.bhfId,
            itemCd: ebmItemData.itemCd,
            itemClsCd: ebmItemData.itemClsCd,
            itemTyCd: ebmItemData.itemTyCd,
            itemNm: ebmItemData.itemNm,
            orgnNatCd: ebmItemData.orgnNatCd,
            pkgUnitCd: ebmItemData.pkgUnitCd,
            qtyUnitCd: ebmItemData.qtyUnitCd,
            taxTyCd: ebmItemData.taxTyCd,
            isrcAplcbYn: ebmItemData.isrcAplcbYn,
            useYn: ebmItemData.useYn,
            // Price fields
            dftPrc: ebmItemData.dftPrc,
            grpPrcL1: ebmItemData.grpPrcL1,
            grpPrcL2: ebmItemData.grpPrcL2,
            grpPrcL3: ebmItemData.grpPrcL3,
            grpPrcL4: ebmItemData.grpPrcL4,
            grpPrcL5: ebmItemData.grpPrcL5,
            sftyQty: ebmItemData.sftyQty,
            // Optional fields
            itemStdNm: ebmItemData.itemStdNm,
            btchNo: ebmItemData.btchNo,
            bcd: ebmItemData.bcd,
            addInfo: ebmItemData.addInfo,
            rraModYn: ebmItemData.rraModYn,
            regBhfId: ebmItemData.regBhfId,
            regrNm: ebmItemData.regrNm,
            regrId: ebmItemData.regrId,
            modrNm: ebmItemData.modrNm,
            modrId: ebmItemData.modrId,
          },
        };

        console.log("📦 Updating product with EBM metadata...");
        let productResponseData = null;
        try {
          const productResponse = await http.patch(
            "/v1/store/product",
            productData
          );
          productResponseData = productResponse.data;
          console.log("✅ Product update successful:", productResponseData);

          // Show success toast for product update
          this.$emit("success", {
            title: "Product Updated Successfully",
            message: "Data saved to RRA EBM.",
            type: "success",
          });
        } catch (productError) {
          console.error("❌ Product update failed:", productError);
          const errorMessage = this.extractErrorMessage(productError);
          this.$emit("error", {
            title: "Product Update Failed",
            message: errorMessage,
            type: "error",
          });
          return;
        }

        // Check if product_purchase_id is available - if not, return after product save
        if (!this.hasProductPurchaseId) {
          console.log(
            "📦 Product Purchase ID not available, returning after product save..."
          );
          this.$emit("loading", false);

          this.$emit("close");
          return;
        }

        // Check if purchase data is already saved - if so, return after product save
        if (this.hasPurchaseEbmMetadata) {
          console.log(
            "📦 Purchase data already saved, returning after product save..."
          );
          this.$emit("loading", false);
          this.$emit("close");
          return;
        }

        // 2. Handle purchase data based on different scenarios
        if (this.fetchedPurchaseData) {
          // Has existing purchase
          if (!this.hasPurchaseEbmMetadata) {
            // Has existing purchase but no purchase EBM metadata: update the purchase with EBM metadata
            console.log("📦 Updating existing purchase with EBM metadata...");

            // Create proper itemList for TrnsPurchaseSaveReq
            const itemList = [
              {
                itemSeq: 1,
                itemClsCd: ebmItemData.itemClsCd,
                itemNm: ebmItemData.itemNm,
                pkg: 1,
                qtyUnitCd: ebmItemData.qtyUnitCd,
                qty: Number(this.formData.ebmPurchase.qty),
                prc: Number(ebmPurchaseData.prc),
                splyAmt:
                  Number(ebmPurchaseData.prc) *
                  Number(this.formData.ebmPurchase.qty),
                dcRt: 0.0,
                dcAmt: 0.0,
                taxblAmt:
                  Number(ebmPurchaseData.prc) *
                    Number(this.formData.ebmPurchase.qty) -
                  0.0, // splyAmt - dcAmt
                taxTyCd: ebmItemData.taxTyCd,
                taxAmt:
                  (Number(ebmPurchaseData.prc) *
                    Number(this.formData.ebmPurchase.qty) -
                    0.0) *
                  ((ebmItemData.taxTyCd === "B"
                    ? Number(ebmPurchaseData.taxRtB)
                    : 0) /
                    100),
                totAmt:
                  Number(ebmPurchaseData.prc) *
                    Number(this.formData.ebmPurchase.qty) -
                  0.0 +
                  (Number(ebmPurchaseData.prc) *
                    Number(this.formData.ebmPurchase.qty) -
                    0.0) *
                    ((ebmItemData.taxTyCd === "B"
                      ? Number(ebmPurchaseData.taxRtB)
                      : 0) /
                      100),
                itemCd:
                  ebmItemData.itemCd ||
                  productResponseData?.rra_ebm_metadata?.itemCd ||
                  this.inventoryItem?.product?.item_sku ||
                  this.inventoryItem?.product?.barcode ||
                  "",
                bcd: ebmItemData.bcd || "",
                spplrItemClsCd: ebmItemData.itemClsCd || "",
                spplrItemCd:
                  ebmItemData.itemCd ||
                  productResponseData?.rra_ebm_metadata?.itemCd ||
                  this.inventoryItem?.product?.item_sku ||
                  this.inventoryItem?.product?.barcode ||
                  "",
                spplrItemNm: ebmItemData.itemNm || "",
                pkgUnitCd: ebmItemData.pkgUnitCd,
                itemExprDt: "",
                isrccCd: null,
                isrccNm: null,
                isrcRt: null,
                isrcAmt: null,
              },
            ];

            // Validate totItemCnt cannot be zero
            if (ebmPurchaseData.totItemCnt === 0) {
              throw new Error("Item count cannot be zero");
            }

            // Get product's unit price for comparison
            const productUnitPrice = this.inventoryItem?.price?.unit_price;

            // Validate dftPrc matches product unit price
            if (
              productUnitPrice &&
              ebmItemData.dftPrc &&
              Math.abs(ebmItemData.dftPrc - productUnitPrice) > 0.01
            ) {
              const errorMessage = `Sale price mismatch detected! EBM sale unit price (${ebmItemData.dftPrc.toFixed(
                2
              )}) does not match store product unit price (${productUnitPrice.toFixed(
                2
              )}). Please verify your EBM data. Change the sale price in the app`;
              this.$emit("error", {
                title: "Sale Unit Price Mismatch",
                message: errorMessage,
                type: "error",
              });
              return;
            }

            if (!ebmPurchaseData.qty) {
              alert(`Item count cannot be zero
              editable quantity: ${this.editableQuantity}
              fetched quantity: ${this.fetchedPurchaseData.quantity}
              inventory quantity: ${this.inventoryItem.quantity}
              form data quantity: ${this.formData.ebmPurchase.qty}
              `);

              throw new Error("Item count cannot be zero");
            }

            const purchaseData = {
              product_purchase_id: this.fetchedPurchaseData.id,
              quantity: ebmPurchaseData.qty,
              unit_price: ebmPurchaseData.prc,
              currency: "RWF",
              rra_ebm_metadata: {
                // Required fields for TrnsPurchaseSaveReq
                tin: ebmPurchaseData.tin,
                bhfId: ebmPurchaseData.bhfId,
                invcNo: Number(ebmPurchaseData.invcNo),
                orgInvcNo: Number(ebmPurchaseData.orgInvcNo),
                regTyCd: ebmPurchaseData.regTyCd,
                pchsTyCd: ebmPurchaseData.pchsTyCd,
                rcptTyCd: ebmPurchaseData.rcptTyCd,
                rctTyCd: ebmPurchaseData.rcptTyCd, // Backwards compatibility
                pmtTyCd: ebmPurchaseData.pmtTyCd,
                pchsSttsCd: ebmPurchaseData.pchsSttsCd,
                pchsDt: ebmPurchaseData.pchsDt,
                totItemCnt: Number(ebmPurchaseData.totItemCnt),
                taxblAmtA: Number(ebmPurchaseData.taxblAmtA),
                taxblAmtB:
                  Number(ebmPurchaseData.prc) * Number(ebmPurchaseData.qty) -
                  0.0, // splyAmt - dcAmt
                taxblAmtC: Number(ebmPurchaseData.taxblAmtC),
                taxblAmtD: Number(ebmPurchaseData.taxblAmtD),
                taxRtA: Number(ebmPurchaseData.taxRtA),
                taxRtB:
                  ebmItemData.taxTyCd === "B"
                    ? Number(ebmPurchaseData.taxRtB)
                    : 0,
                taxRtC: Number(ebmPurchaseData.taxRtC),
                taxRtD: Number(ebmPurchaseData.taxRtD),
                taxAmtA: Number(ebmPurchaseData.taxAmtA),
                taxAmtB:
                  (Number(ebmPurchaseData.prc) * Number(ebmPurchaseData.qty) -
                    0.0) *
                  ((ebmItemData.taxTyCd === "B"
                    ? Number(ebmPurchaseData.taxRtB)
                    : 0) /
                    100),
                taxAmtC: Number(ebmPurchaseData.taxAmtC),
                taxAmtD: Number(ebmPurchaseData.taxAmtD),
                totTaxblAmt:
                  Number(ebmPurchaseData.prc) * Number(ebmPurchaseData.qty) -
                  0.0, // splyAmt - dcAmt
                totTaxAmt:
                  (Number(ebmPurchaseData.prc) * Number(ebmPurchaseData.qty) -
                    0.0) *
                  ((ebmItemData.taxTyCd === "B"
                    ? Number(ebmPurchaseData.taxRtB)
                    : 0) /
                    100),
                totAmt:
                  Number(ebmPurchaseData.prc) * Number(ebmPurchaseData.qty) -
                  0.0 +
                  (Number(ebmPurchaseData.prc) * Number(ebmPurchaseData.qty) -
                    0.0) *
                    ((ebmItemData.taxTyCd === "B"
                      ? Number(ebmPurchaseData.taxRtB)
                      : 0) /
                      100),
                regrNm: ebmPurchaseData.regrNm,
                regrId: ebmPurchaseData.regrId,
                modrNm: ebmPurchaseData.modrNm,
                modrId: ebmPurchaseData.modrId,
                itemList: itemList,
                // Optional fields
                spplrTin: ebmPurchaseData.spplrTin,
                spplrBhfId: ebmPurchaseData.spplrBhfId,
                spplrNm: ebmPurchaseData.spplrNm,
                spplrInvcNo: Number(ebmPurchaseData.spplrInvcNo),
                spplrSdcId: ebmPurchaseData.spplrSdcId || "",
                spplrItemCd:
                  ebmItemData.itemCd ||
                  productResponseData?.rra_ebm_metadata?.itemCd ||
                  this.inventoryItem?.product?.item_sku ||
                  this.inventoryItem?.product?.barcode ||
                  "",
                cfmDt: ebmPurchaseData.cfmDt,
                wrhsDt: ebmPurchaseData.wrhsDt || "",
                cnclReqDt: ebmPurchaseData.cnclReqDt || "",
                cnclDt: ebmPurchaseData.cnclDt || "",
                rfdDt: ebmPurchaseData.rfdDt || "",
                remark: ebmPurchaseData.remark || "",
              },
            };

            try {
              const purchaseResponse = await http.patch(
                "/v1/store/product/purchase",
                purchaseData
              );
              console.log(
                "✅ Purchase update successful:",
                purchaseResponse.data
              );

              // Show success toast for purchase update
              this.$emit("success", {
                title: "Purchase Updated Successfully",
                message: "Data saved to RRA EBM.",
                type: "success",
              });
            } catch (purchaseError) {
              console.error("❌ Purchase update failed:", purchaseError);
              const errorMessage = this.extractErrorMessage(purchaseError);
              this.$emit("error", {
                title: "Purchase Update Failed",
                message: errorMessage,
                type: "error",
              });
              return;
            }
          } else {
            // Has existing purchase and existing purchase EBM metadata: Do not send the purchase update request, just save the EBM item
            console.log(
              "✅ Purchase already has EBM metadata - skipping purchase update"
            );
          }
        } else {
          // Has no existing purchase: just return (only product update was done)
          console.log("ℹ️ No existing purchase found - only product updated");
        }

        // Show success toast for complete save operation
        this.$emit("success", {
          title: "EBM Data Saved Successfully",
          message:
            "Product and purchase data have been saved with EBM metadata.",
          type: "success",
        });

        this.$emit("saved", {
          ebmItem: ebmItemData,
          ebmPurchase: ebmPurchaseData,
        });

        this.handleClose();
      } catch (error) {
        console.error("Error saving EBM data:", error);
        this.$emit("error", error);
      } finally {
        this.$emit("loading", false);
      }
    },

    hasEbmItemChanges() {
      if (!this.originalData) return true;

      const original = this.originalData.ebmItem;
      const current = this.formData.ebmItem;

      return JSON.stringify(original) !== JSON.stringify(current);
    },

    hasEbmPurchaseChanges() {
      if (!this.originalData) return true;

      const original = this.originalData.ebmPurchase;
      const current = this.formData.ebmPurchase;

      return JSON.stringify(original) !== JSON.stringify(current);
    },

    handleClose() {
      console.log("EbmDataEditorModal: Modal closing, resetting state");
      this.resetModalState();
      this.$emit("close");
    },

    resetModalState() {
      console.log("EbmDataEditorModal: Resetting modal state");
      this.validationErrors = [];
      this.activeTab = "product";
      this.isLoadingData = false;
      this.fetchedProductData = null;
      this.fetchedPurchaseData = null;
      this.originalData = null;
      this.showAdvancedFields = false;
      this.showLevel2Fields = false;
      this.showLevel3Fields = false;
      this.showPurchaseLevel2Fields = false;
      this.showPurchaseLevel3Fields = false;
      this.showPurchaseLevel4Fields = false;
      this.showPurchaseLevel5Fields = false;
      this.showPreviewProductDetails = false;
      this.showPreviewPurchaseDetails = false;
      this.showQuantityMismatchDetails = false;
      this.loadingMessage = "Loading EBM data...";
      this.editableQuantity = 0;
      this.selectedQuantityOptions = []; // Reset checkbox selections
      this.selectedQuantityOption = "original"; // Reset to default
      // Reset form data to initial state
      this.formData = {
        ebmItem: {
          tin: "",
          bhfId: "",
          itemCd: "",
          itemClsCd: "",
          itemTyCd: "2",
          itemNm: "",
          orgnNatCd: "RW",
          pkgUnitCd: "NT",
          qtyUnitCd: "U",
          taxTyCd: "B",
          isrcAplcbYn: "N",
          useYn: "Y",
          dftPrc: 0,
          itemStdNm: null,
          btchNo: null,
          bcd: null,
          addInfo: null,
          grpPrcL1: 0,
          grpPrcL2: 0,
          grpPrcL3: 0,
          grpPrcL4: 0,
          grpPrcL5: 0,
          sftyQty: 0,
          rraModYn: "N",
          regBhfId: null,
          regrNm: "",
          regrId: "",
          modrNm: "",
          modrId: "",
        },
        ebmPurchase: {
          tin: "",
          bhfId: "",
          regTyCd: "A",
          pchsTyCd: "N",
          rcptTyCd: "P",
          pmtTyCd: "01",
          pchsSttsCd: "02",
          pchsDt: "",
          regrNm: "Admin",
          regrId: "Admin",
          modrNm: "Admin",
          modrId: "Admin",
          invcNo: 0,
          orgInvcNo: 0,
          totItemCnt: 1,
          taxblAmtA: 0,
          taxblAmtB: 0,
          taxblAmtC: 0,
          taxblAmtD: 0,
          taxRtA: 0,
          taxRtB: 18,
          taxRtC: 0,
          taxRtD: 0,
          taxAmtA: 0,
          taxAmtB: 0,
          taxAmtC: 0,
          taxAmtD: 0,
          totTaxblAmt: 0,
          totTaxAmt: 0,
          totAmt: 0,
          spplrTin: "999999999",
          spplrBhfId: "00",
          spplrNm: "Supplier",
          spplrInvcNo: 1,
          spplrSdcId: null,
          spplrItemCd: null,
          cfmDt: null,
          wrhsDt: null,
          cnclReqDt: null,
          cnclDt: null,
          rfdDt: null,
          remark: null,
          itemList: [],
        },
      };
    },
  },
};
</script>

<style scoped>
.ebm-data-editor-modal {
  min-height: 0;
  position: relative;
}

:deep(.ebm-data-editor-modal__body) {
  padding: 0;
}

.ebm-data-editor-modal > .flex-1 {
  padding: 1.1rem;
}

/* Progressive Disclosure Animations */
@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.animate-fadeIn {
  animation: fadeIn 0.3s ease-out;
}

/* Smooth transitions for expandable sections */
.transition-all {
  transition: all 0.3s ease-in-out;
}

/* Enhanced focus states */
input:focus,
select:focus {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.15);
}

/* Gradient backgrounds for better visual hierarchy */
.bg-gradient-to-r {
  background-image: linear-gradient(to right, var(--tw-gradient-stops));
}

/* Custom scrollbar for better UX */
.overflow-y-auto::-webkit-scrollbar {
  width: 6px;
}

.overflow-y-auto::-webkit-scrollbar-track {
  background: transparent;
}

.overflow-y-auto::-webkit-scrollbar-thumb {
  background-color: #cbd5e0;
  border-radius: 3px;
}

.overflow-y-auto::-webkit-scrollbar-thumb:hover {
  background-color: #a0aec0;
}

/* Dark mode scrollbar */
.dark .overflow-y-auto::-webkit-scrollbar-thumb {
  background-color: #4a5568;
}

.dark .overflow-y-auto::-webkit-scrollbar-thumb:hover {
  background-color: #718096;
}

/* Enhanced button hover effects */
button:hover {
  transform: translateY(-1px);
}

button:active {
  transform: translateY(0);
}

/* Level indicator styling */
.level-indicator {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
}

/* Responsive grid improvements */
@media (max-width: 768px) {
  .grid-cols-1.md\\:grid-cols-2.lg\\:grid-cols-3 {
    grid-template-columns: 1fr;
  }

  .md\\:col-span-2 {
    grid-column: span 1;
  }
}

/* Enhanced form field styling */
input,
select {
  transition: all 0.2s ease-in-out;
}

input:hover,
select:hover {
  border-color: #93c5fd;
}

/* Locked field styling */
input:disabled,
select:disabled {
  background: linear-gradient(135deg, #f3f4f6 0%, #e5e7eb 100%);
}

.dark input:disabled,
.dark select:disabled {
  background: linear-gradient(135deg, #374151 0%, #4b5563 100%);
}

/* Section header enhancements */
.section-header {
  background: linear-gradient(
    135deg,
    rgba(255, 255, 255, 0.1) 0%,
    rgba(255, 255, 255, 0.05) 100%
  );
  backdrop-filter: blur(10px);
}

/* Icon animations */
.rotate-180 {
  transform: rotate(180deg);
}

/* Enhanced shadow effects */
.shadow-sm {
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06);
}

.shadow-md {
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1),
    0 2px 4px -1px rgba(0, 0, 0, 0.06);
}

.shadow-lg {
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1),
    0 4px 6px -2px rgba(0, 0, 0, 0.05);
}

/* Focus ring improvements */
.focus\\:ring-2:focus {
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

/* Border radius consistency */
.rounded-lg {
  border-radius: 0.5rem;
}

.rounded-xl {
  border-radius: 0.75rem;
}

/* Spacing improvements */
.space-y-6 > * + * {
  margin-top: 1.5rem;
}

.space-y-4 > * + * {
  margin-top: 1rem;
}

/* Typography enhancements */
.text-lg {
  font-size: 1.125rem;
  line-height: 1.75rem;
}

.text-sm {
  font-size: 0.875rem;
  line-height: 1.25rem;
}

.text-xs {
  font-size: 0.75rem;
  line-height: 1rem;
}

/* Color scheme consistency */
.text-blue-600 {
  color: #2563eb;
}

.text-green-600 {
  color: #059669;
}

.text-purple-600 {
  color: #9333ea;
}

.text-orange-600 {
  color: #ea580c;
}

/* Dark mode color adjustments */
.dark .text-blue-400 {
  color: #60a5fa;
}

.dark .text-green-400 {
  color: #4ade80;
}

.dark .text-purple-400 {
  color: #a78bfa;
}

.dark .text-orange-400 {
  color: #fb923c;
}
</style>
